/* Velvet Shieldz stepper (Phase 2.2) — guided-stepper v2 per design-reference-v2.html:
   progress rail + traveling preview panel + None pill + CONTINUE-only advance.
   SUPERSEDES the Phase-2 sticky strip (removed).

   HARD RULES (audit-bound):
   - configurator.js is NEVER touched and none of its globals (CFG_DATA*, state)
     are read or written. Everything here is DOM observation + delegation, with
     dispatched clicks on configurator-owned pills where its own toggle must run.
   - ROOT-CAUSE FIX: root click + keydown delegation runs in CAPTURE phase —
     configurator.js's model-item handler calls e.stopPropagation()
     (configurator.js:1143) which kills bubble delegation. Capture fires
     root→target BEFORE that handler, so it cannot be suppressed. All state is
     still read AFTER configurator's handlers via setTimeout(0) deferrals.
   - No price display anywhere in the stepper (the strip's .money span was
     removed on purpose); this file never touches [data-cfg-price-*] or .money.
   - Steps collapse via .is-collapsed (max-height/overflow/opacity on step
     CHILDREN — CSS in product-info.liquid). NEVER display:none: the textarea
     autosize in configurator.js reads scrollHeight.
   - The preview panel is a stepper-OWNED node; moving IT between steps is
     allowed. The configurator's [data-configurator-mobile-preview] canvas is
     never moved, cloned, or duplicated — only drawImage-mirrored. */
(function () {
  'use strict';

  function initStepper(root) {
    if (!root || root.dataset.vsStepper === '2') return;
    var steps = {};
    root.querySelectorAll('.cfg-step[data-cfg-step]').forEach(function (el) {
      steps[el.getAttribute('data-cfg-step')] = el;
    });
    if (!steps['1'] || !steps['2'] || !steps['3'] || !steps['4']) return;
    root.dataset.vsStepper = '2';

    // --- Handles ----------------------------------------------------------
    // First .cfg-dropdown inside Step 1 is the model picker (audit invariant).
    var modelDropdown = steps['1'].querySelector('.cfg-dropdown');
    var modelLabel    = modelDropdown ? modelDropdown.querySelector('.cfg-dropdown__label') : null;
    var colorNameEl   = root.querySelector('.cfg-color-name');
    var textarea      = root.querySelector('.cfg-textarea');
    var uploadThumb   = root.querySelector('.cfg-upload__thumb');
    var uploadBtn     = root.querySelector('.cfg-upload__btn');
    var casesEl       = root.querySelector('.cfg-cases');
    var removeModal   = root.querySelector('[data-cfg-remove-modal]');
    var nonePill      = root.querySelector('[data-cfg-none]');
    var continueBtns  = root.querySelectorAll('[data-cfg-continue]');
    var panel        = root.querySelector('[data-cfg-preview-panel]');
    var mirrorCanvas = root.querySelector('[data-cfg-mirror-canvas]');
    var sourceCanvas = root.querySelector('[data-configurator-mobile-preview]');
    var textWrapper  = root.querySelector('.cfg-text-wrapper');
    var fontDropdown = root.querySelector('.cfg-font-dropdown');
    var fontTrigger  = fontDropdown ? fontDropdown.querySelector('.cfg-dropdown__trigger') : null;
    var fontLabel    = fontDropdown ? fontDropdown.querySelector('.cfg-dropdown__label') : null;
    var noteWrap     = root.querySelector('.cfg-note');
    var previewArea  = root.querySelector('.cfg-mobile-canvas-row');
    var textChip     = root.querySelector('[data-cfg-subchip="text"]');
    var fontChip     = root.querySelector('[data-cfg-subchip="font"]');
    var textChipVal  = root.querySelector('[data-cfg-subchip-value="text"]');
    var fontChipVal  = root.querySelector('[data-cfg-subchip-value="font"]');
    var sizeConfirm  = root.querySelector('[data-cfg-sizeconfirm]');

    var previewChip  = root.querySelector('[data-cfg-subchip="preview"]');

    // Phase 2.9 sub-flow state (Text mode): chip-collapse latches. UI-transient
    // — cleared by model-change reset and per-case recompute. Phase 3.1 adds
    // the size/position confirm latch + its paced-scroll timer.
    var subTextDone = false;
    var subFontDone = false;
    var subPreviewDone = false;
    var confirmScrollTimer = null;
    function resetSubFlow() {
      subTextDone = false;
      subFontDone = false;
      subPreviewDone = false;
      cancelConfirmScroll();
      if (sizeConfirm) sizeConfirm.classList.remove('is-ticked');
    }
    // Push 5 tombstone (audit §2): the confirm-scroll SEQUENCER that lived
    // here — transitionend(max-height) listener on root, 320ms fallback
    // timer, 80ms beat — is DELETED. That pacing predates settledScroll's
    // per-frame target re-read: computeScrollY re-reads the live rect every
    // frame, so a scroll started on the tap's own frame converges on the
    // post-collapse offset with no stale-coordinate risk. The confirm
    // handler now calls settledScroll directly. cancelConfirmScroll stays —
    // settledScroll and the chip handlers still invoke it as the cancel-net
    // (confirmEndHandler can no longer be set; the branch is inert but kept
    // so every caller keeps working unchanged).
    var confirmEndHandler = null;
    function cancelConfirmScroll() {
      if (confirmScrollTimer) { clearTimeout(confirmScrollTimer); confirmScrollTimer = null; }
      if (confirmEndHandler) { root.removeEventListener('transitionend', confirmEndHandler); confirmEndHandler = null; }
    }
    // Cosmetic only: the ✓ flash on the confirm control. Its timer is
    // deliberately OUTSIDE the cancelConfirmScroll net — the handler calls
    // settledScroll right after arming this, and settledScroll fires that
    // net; the flash must survive it. resetSubFlow/chip taps remove
    // is-ticked directly, so a pending removal here is a harmless no-op.
    var tickFlashTimer = null;
    function armConfirmScroll() {
      if (tickFlashTimer) clearTimeout(tickFlashTimer);
      tickFlashTimer = setTimeout(function () {
        tickFlashTimer = null;
        if (sizeConfirm) sizeConfirm.classList.remove('is-ticked');
      }, 340); // flash window ≈ the old collapse-plus-beat duration
    }

    // --- Shared helpers (Phase 2.6) ---------------------------------------
    function isTypingTarget() {
      var ae = document.activeElement;
      // Push 6: [type="file"] excluded — this guard exists to avoid scrolling
      // under an open keyboard, and a file picker opens none. On the mobile
      // upload path focus can rest on .cfg-mobile-file when bg-removal
      // completes, which silently swallowed fireUploadScroll's completion
      // scroll (the probable Push-5 phone-QA miss; phone re-test pending).
      return !!(ae && ae.matches && ae.matches('textarea, select, input:not([type="hidden"]):not([type="file"])'));
    }
    var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    // --- SCROLL SINGLE-AUTHORITY (Phase 3.1: self-correcting loop) ---------
    // The ONLY scroll mechanism in this file. No transitionend gating: the
    // loop starts one rAF after the request and RE-READS the target's live
    // position EVERY frame, so the 260ms collapse/expand transitions and the
    // 360ms uncap timer bend the path instead of stranding the viewport at a
    // stale offset (the step-3→4 bounce). One in-flight loop; a new call
    // cancels the previous. Reduced motion: wait out the uncap timer, then
    // jump instantly to the freshly computed offset.
    var scrollRafId = null;
    var scrollJumpTimer = null;
    function cancelQueuedScroll() {
      if (scrollRafId != null) { cancelAnimationFrame(scrollRafId); scrollRafId = null; }
      if (scrollJumpTimer) { clearTimeout(scrollJumpTimer); scrollJumpTimer = null; }
    }
    function scrollMarginOf(el) {
      var m = parseFloat(getComputedStyle(el).scrollMarginTop);
      if (m > 0) return m;
      var sticky = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height'));
      return (isNaN(sticky) ? 0 : sticky) + 16; // Phase 4: 2px line + gap (was +72 dot rail)
    }
    function targetYOf(el) {
      return el.getBoundingClientRect().top + window.scrollY - scrollMarginOf(el);
    }
    // Phase 1.5 Fix 3: alignment option. 'top' (default) = element top under
    // the sticky area (every pre-existing call site). 'bottom' = element's
    // BOTTOM edge ~16px above the viewport bottom; opts.keepVisible guards
    // the fallback — if bottom-alignment would push that element's top above
    // the sticky area, fall back to keepVisible-top-under-sticky.
    function computeScrollY(target, opts) {
      var align = (opts && opts.align) ?? 'top';
      // Phase 3.5 Fix 2: 'center' = target vertically centered; opts.fitBottom
      // guards small screens — if that element's bottom would fall outside the
      // viewport, fall back to aligning ITS bottom 16px above the viewport
      // bottom (keeps the confirm button in view, note below the fold).
      if (align === 'center') {
        var cRect = target.getBoundingClientRect();
        var cy = cRect.top + window.scrollY - (window.innerHeight - cRect.height) / 2;
        var fb = opts && opts.fitBottom;
        if (fb && !fb.hidden) {
          var fbBottomDoc = fb.getBoundingClientRect().bottom + window.scrollY;
          if (fbBottomDoc - cy > window.innerHeight - 16) {
            cy = fbBottomDoc - window.innerHeight + 16;
          }
        }
        return Math.max(0, cy);
      }
      if (align === 'bottom') {
        var rect = target.getBoundingClientRect();
        var y = rect.bottom + window.scrollY - window.innerHeight + 16;
        var keep = opts && opts.keepVisible;
        if (keep) {
          var keepTopDoc = keep.getBoundingClientRect().top + window.scrollY;
          var minTop = scrollMarginOf(keep);
          if (keepTopDoc - y < minTop) y = keepTopDoc - minTop; // fallback
        }
        return Math.max(0, y);
      }
      return Math.max(0, targetYOf(target));
    }
    function settledScroll(target, opts) {
      if (!target) return;
      cancelQueuedScroll();
      cancelConfirmScroll(); // Task 3b: one net — a new scroll kills pending pacing
      // Task 3a: opts.ifNeeded — skip entirely if the target is already >=70%
      // visible in the band between the sticky area and the viewport bottom.
      if (opts && opts.ifNeeded) {
        var vr = target.getBoundingClientRect();
        var stickyV = parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--sticky-area-height'));
        var bandTop = (isNaN(stickyV) ? 0 : stickyV) + 16;
        var visiblePx = Math.min(vr.bottom, window.innerHeight) - Math.max(vr.top, bandTop);
        if (vr.height > 0 && visiblePx / vr.height >= 0.7) return;
      }
      if (prefersReduced) {
        scrollJumpTimer = setTimeout(function () {
          scrollJumpTimer = null;
          window.scrollTo(0, computeScrollY(target, opts));
          if (opts && opts.onDone) opts.onDone();
        }, 420); // past the 360ms uncap timer — layout is final
        return;
      }
      var DURATION = 360;
      function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
      scrollRafId = requestAnimationFrame(function (startNow) {
        var startY = window.scrollY;
        function frame(now) {
          scrollRafId = null;
          var t = Math.min(1, (now - startNow) / DURATION);
          var y = computeScrollY(target, opts); // live re-read — the fix
          if (t >= 1) {
            window.scrollTo(0, y); // snap to the final, current offset
            if (opts && opts.onDone) opts.onDone();
            return;
          }
          window.scrollTo(0, startY + (y - startY) * easeOutCubic(t));
          scrollRafId = requestAnimationFrame(frame);
        }
        frame(startNow);
      });
    }
    function fontIsPlaceholder() {
      return !!(fontLabel && fontLabel.classList.contains('cfg-dropdown__label--placeholder'));
    }
    function thumbVisible() {
      return !!(uploadThumb && !uploadThumb.hidden);
    }
    function warn(el)      { if (el) el.classList.add('vs-warn'); }
    function clearWarn(el) { if (el) el.classList.remove('vs-warn'); }

    // --- Per-case stepper state (stepper-local; configurator untouched) ---
    // "Assume nothing carries over": unseen case index starts fresh; case
    // removal clears everything. cur = the single open step (accordion).
    var caseFlags = {};
    var activeCase = 0;
    // B.4: per-case "user explicitly picked a wallet font" — written by the
    // wallet-font-dropdown widget, read by the Finish gate. Cleared on case
    // removal (reindex) alongside the widget's own reset.
    var walletFontPicked = {};
    // Phase B (per-case snapshot harness — INVISIBLE, read-only): last-known
    // config + a downscaled art thumbnail per case, for a future multi-case
    // finale. Populated from the SAME live DOM updateFinaleContent() reads.
    // Never reassigned (reindexed IN PLACE on remove) so the debug ref stays live.
    var caseSnapshots = {};
    // Case-removal fix (2026-07-28): the four per-case stores above/below are
    // keyed by case INDEX. Removal used to WIPE them all ("removal reindexes —
    // drop all per-case stepper state"), which reset every SURVIVING case's
    // step progress/snapshot/font state — the case-1-resets-when-case-2-is-
    // removed bug. Now we capture the removed index at [data-cfg-remove]
    // click time (the modal confirm carries no index) and SHIFT keys > idx
    // down by one, mirroring configurator's designs.splice(i,1) exactly
    // (configurator itself preserves survivors: saveActiveDesign +
    // loadDesign(activeIndex) — audited). Maps are mutated in place so debug
    // refs (__vsSnap) stay live.
    var pendingRemoveIdx = -1;
    root.addEventListener('click', function (e) {
      var rm = e.target.closest && e.target.closest('[data-cfg-remove]');
      if (rm) pendingRemoveIdx = parseInt(rm.getAttribute('data-cfg-remove'), 10);
      // modal cancel / scrim close abandons the pending removal
      if (e.target.closest && e.target.closest('[data-cfg-modal-cancel]')) pendingRemoveIdx = -1;
    }, true);
    function reindexCaseMap(m, removed) {
      var k, n, max = -1;
      for (k in m) { if (Object.prototype.hasOwnProperty.call(m, k)) { n = parseInt(k, 10); if (n > max) max = n; } }
      if (removed < 0 || isNaN(removed)) { // index unknown — legacy fallback, never expected
        for (k in m) { if (Object.prototype.hasOwnProperty.call(m, k)) delete m[k]; }
        return;
      }
      delete m[removed];
      for (n = removed + 1; n <= max; n++) {
        if (Object.prototype.hasOwnProperty.call(m, n)) { m[n - 1] = m[n]; delete m[n]; }
        else { delete m[n - 1]; }
      }
    }
    function flags() {
      var f = caseFlags[activeCase];
      if (!f) {
        f = { confirmed: { 1: false, 2: false, 3: false }, cur: 1 };
        caseFlags[activeCase] = f;
      }
      return f;
    }

    // --- Completion model (pure DOM reads + confirmed-by-CONTINUE flags) --
    function activeMode() {
      var pill = root.querySelector('.cfg-pill.is-active');
      return pill ? pill.getAttribute('data-personalization') : null;
    }
    function step3Content() {
      var mode = activeMode();
      if (mode === 'Text')  return !!(textarea && textarea.value.trim());
      if (mode === 'Image') return !!(uploadThumb && !uploadThumb.hidden);
      return false;
    }
    // Step 3 stays valid with no mode (None = clean suede, no fee) or with a
    // mode that has real content. Mode active + empty content invalidates the
    // confirmation until content exists again.
    function step3Valid() {
      return activeMode() == null ? true : step3Content();
    }
    function isDone(n) {
      if (n === 4) return false;
      var f = flags();
      if (!f.confirmed[n]) return false;
      if (n === 3) return step3Valid();
      return true;
    }
    function valueFor(n) {
      if (n === 1) return modelLabel ? modelLabel.textContent.trim() : '';
      if (n === 2) return colorNameEl ? colorNameEl.textContent.trim() : '';
      if (n === 3) {
        var mode = activeMode();
        if (mode === 'Text') {
          var t = (textarea ? textarea.value.trim() : '').replace(/\s+/g, ' ');
          return t ? '“' + (t.length > 16 ? t.slice(0, 16) + '…' : t) + '”' : 'Text';
        }
        if (mode === 'Image') return 'Custom image';
        return 'Basic';
      }
      if (n === 4) {
        // Push 9 Task 2: done-row value for COMPLETE THE PAIR — Yes/No from
        // the relocated real wallet checkbox (walletToggle, assigned in the
        // finale block below; var-hoisted, and the boot renderAll runs after
        // that assignment). Same live signal every finale writer reads, and
        // per-case for free: configurator.js drives this checkbox on every
        // case load, and each path that flips it (taps in the open step,
        // case-switch, add-case reset) re-runs renderAll before this row
        // redisplays.
        return (walletToggle && walletToggle.checked) ? 'Yes' : 'No';
      }
      return '';
    }

    // --- Collapse / expand (children mechanism from Phase 2) --------------
    // Phase 2.5 measured-height technique: expand animates each child's
    // max-height 0 → measured scrollHeight, then CLEARS it (max-height:none)
    // so an open step can never clip content at any height; collapse commits
    // the measured px first (forced reflow) then lets the CSS 0 take over so
    // the height visibly animates instead of snapping.
    var stepAnimTimers = {};
    function stepChildren(step) {
      return Array.prototype.filter.call(step.children, function (c) {
        return !c.classList.contains('cfg-step__header');
      });
    }
    function clearStepTimer(n) {
      if (stepAnimTimers[n]) { clearTimeout(stepAnimTimers[n]); stepAnimTimers[n] = null; }
    }
    function expandStep(n) {
      var step = steps[String(n)];
      if (!step || !step.classList.contains('is-collapsed')) return;
      clearStepTimer(n);
      var kids = stepChildren(step);
      step.classList.remove('is-collapsed');
      kids.forEach(function (c) {
        c.style.overflow = 'hidden';
        c.style.maxHeight = c.scrollHeight + 'px';
      });
      stepAnimTimers[n] = setTimeout(function () {
        kids.forEach(function (c) { c.style.maxHeight = ''; c.style.overflow = ''; });
        stepAnimTimers[n] = null;
      }, 360);
      // Phase 5.3: the old synthetic-input "autosize recovery" here was the
      // bug — it made the configurator's autosize MEASURE the field while its
      // panel was still hidden (scrollHeight 0 → inline height:0px, or 140px
      // under boot ordering), and that inline style overrode every CSS rule.
      // Removed; renderAll clears stale inline heights instead.
    }
    function collapseStep(n) {
      var step = steps[String(n)];
      if (!step || step.classList.contains('is-collapsed')) return;
      clearStepTimer(n);
      var kids = stepChildren(step);
      kids.forEach(function (c) { c.style.maxHeight = c.scrollHeight + 'px'; });
      void step.offsetHeight; // commit start heights so the 0 transition runs
      step.classList.add('is-collapsed');
      kids.forEach(function (c) { c.style.maxHeight = ''; }); // CSS 0 wins now
    }

    // --- Traveling preview panel (accompanies Steps 1–2 only) --------------
    // Phase 3.1: the panel lives INSIDE the step's bordered square, as the
    // step's last child (field → Continue → preview → square edge, mirroring
    // Step 3's order). Phase 2.8 fade-move kept: is-moving 140ms →
    // reposition → is-arriving 180ms. Reduced motion: instant reposition.
    var panelMoveTimer = null;
    // Klaus: Continue reads AFTER the preview, so the panel is inserted BEFORE
    // the step's Continue button rather than appended last. Returns null when a
    // step has no Continue, in which case callers append exactly as before.
    // Plain child loop (no :scope) to match this file's idiom, and DIRECT
    // children only — a nested Continue must never become the anchor.
    function panelAnchor(stepEl) {
      var kids = stepEl.children;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].hasAttribute('data-cfg-continue')) return kids[i];
      }
      return null;
    }
    function movePanel() {
      if (!panel) return;
      var cur = flags().cur;
      // Phase 1.5 Fix 1: the panel accompanies STEP 2 ONLY. Step 1 has no
      // preview; Step 3 shows its own native configurator preview; Step 4 has
      // none. Hide — never destroy. Reopening Step 2 restores it. Its first
      // arrival on the 1→2 transition goes through placeInside(), whose
      // mid-expand cap patch keys on the step's pending uncap timer.
      if (cur !== 2) { panel.hidden = true; return; }
      var stepEl = steps[String(cur)];
      if (!stepEl) return;
      function placeInside() {
        var anchorEl = panelAnchor(stepEl);
        if (anchorEl) stepEl.insertBefore(panel, anchorEl); else stepEl.appendChild(panel);
        // Height patch (Phase 3.1): the step carries no inline max-height —
        // its CHILDREN do, measured by expandStep BEFORE the panel arrived.
        // While the step is inside its 0–360ms expand window (pending uncap
        // timer), give the freshly-arrived panel its own cap+uncap cycle so
        // the collapsed-state child CSS can't clip it mid-transition.
        if (stepAnimTimers[cur] != null) {
          panel.style.maxHeight = panel.scrollHeight + 'px';
          setTimeout(function () { panel.style.maxHeight = ''; }, 360);
        }
      }
      var placed = (panel.parentElement === stepEl && panel.nextElementSibling === panelAnchor(stepEl));
      if (placed) { panel.hidden = false; return; }
      if (prefersReduced || panel.hidden) {
        placeInside();
        panel.hidden = false;
        return;
      }
      if (panelMoveTimer) clearTimeout(panelMoveTimer);
      panel.classList.add('is-moving'); // opacity → 0 over 140ms
      panelMoveTimer = setTimeout(function () {
        placeInside();
        void panel.offsetHeight;
        panel.classList.remove('is-moving');
        panel.classList.add('is-arriving'); // 180ms rise-in
        panelMoveTimer = setTimeout(function () {
          panel.classList.remove('is-arriving');
          panelMoveTimer = null;
        }, 200);
      }, 150);
    }

    // --- Render everything from state --------------------------------------
    function renderAll() {
      var f = flags();
      // Phase 3.5 Fix 5b: while the finale is open, Step 4 collapses to a
      // done-style row (same 260ms machinery) so the page reads: rows 1–4
      // above, finale below. Re-entering the steps hides the finale and this
      // reverts automatically (cur is untouched — prior state restored).
      var finaleOpen = !!(typeof finale !== 'undefined' && finale && !finale.hidden);
      // Phase 3.5 Fix 1: if Step 2 is about to expand this render, place the
      // (stepper-owned) panel inside it FIRST so expandStep's measured
      // heights include it — the square opens at full height in one motion.
      if (panel && f.cur === 2 && steps['2'].classList.contains('is-collapsed')) {
        if (!(panel.parentElement === steps['2'] && panel.nextElementSibling === panelAnchor(steps['2']))) {
          var preAnchor = panelAnchor(steps['2']);
          if (preAnchor) steps['2'].insertBefore(panel, preAnchor); else steps['2'].appendChild(panel);
        }
        panel.hidden = false;
      }
      for (var n = 1; n <= 4; n++) {
        var step = steps[String(n)];
        var open = (f.cur === n) && !(n === 4 && finaleOpen);
        var done = isDone(n);
        var doneRow = (done && !open) || (n === 4 && finaleOpen);
        step.classList.toggle('is-open', open);
        step.classList.toggle('is-done', doneRow);
        step.classList.toggle('is-pending', !doneRow && !open);
        if (done) step.classList.remove('vs-warn'); // Finish-path warn self-clears
        if (open) expandStep(n); else collapseStep(n);
        // Header value (done rows only) + check.
        var header = step.querySelector('.cfg-step__header');
        if (header) {
          var sum = header.querySelector('.cfg-step__summary');
          if (!sum) {
            sum = document.createElement('span');
            sum.className = 'cfg-step__summary';
            var chk = header.querySelector('.cfg-step__check');
            if (chk) header.insertBefore(sum, chk); else header.appendChild(sum);
          }
          // Push 9 Task 2: gate on doneRow, not (done && !open) — identical
          // for steps 1–3, but step 4's done-style row exists ONLY via the
          // Fix-5b finaleOpen clause (isDone(4) is a hard false; no confirmed
          // flag), so the old gate kept its value permanently empty.
          sum.textContent = doneRow ? valueFor(n) : '';
          // Tick shows ONLY on closed done rows (far right, after the value —
          // markup order title → summary → check). A reopened done step shows
          // no tick anywhere (Phase 2.5).
          var check = header.querySelector('.cfg-step__check');
          if (check) {
            if (done && !open) {
              if (!check.classList.contains('is-done')) {
                check.classList.add('is-done');
                check.textContent = '✓';
              }
              check.hidden = false;
            } else {
              check.classList.remove('is-done');
              check.textContent = '';
              check.hidden = true;
            }
          }
        }
        // Phase 4 Task 1d: collapsed done-rows are the jump mechanism now —
        // the header is a real tap target (>=44px row per Task 6).
        if (header) {
          if (doneRow) {
            header.setAttribute('role', 'button');
            header.setAttribute('tabindex', '0');
            header.setAttribute('aria-label', 'Go to step ' + n);
          } else {
            header.removeAttribute('role');
            header.removeAttribute('tabindex');
            header.removeAttribute('aria-label');
          }
        }
      }
      // Phase 7 Task 3: the four-segment rail driver is DELETED — the numbered
      // header circles (pure CSS off is-open/is-done/is-pending) are the
      // progress indicator now.
      // None pill reflects configurator state: active when NO mode pill is.
      var curMode = activeMode();
      if (nonePill) {
        var noneOn = (curMode == null);
        nonePill.classList.toggle('is-active', noneOn);
        nonePill.setAttribute('aria-pressed', noneOn ? 'true' : 'false');
      }
      // Phase 2.9: Step-3 mode classes drive the CSS-only flex reorder, and
      // the sub-collapse chip states (Text mode only). Chips self-invalidate
      // when their underlying value vanishes.
      var step3El = steps['3'];
      step3El.classList.toggle('cfg-mode-text', curMode === 'Text');
      step3El.classList.toggle('cfg-mode-image', curMode === 'Image');
      // Phase 3.1: a Text↔Image (or mode↔None) switch clears the confirmed
      // size/position latch so the preview is visible when its content changes.
      if (typeof renderAll.lastMode !== 'undefined' && renderAll.lastMode !== curMode && subPreviewDone) {
        subPreviewDone = false;
        cancelConfirmScroll();
      }
      renderAll.lastMode = curMode;
      var textDoneVis = curMode === 'Text' && subTextDone && !!(textarea && textarea.value.trim());
      var fontDoneVis = curMode === 'Text' && subFontDone && !fontIsPlaceholder();
      var prevDoneVis = (curMode === 'Text' || curMode === 'Image') && subPreviewDone && step3Content();
      step3El.classList.toggle('sub-text-done', textDoneVis);
      step3El.classList.toggle('sub-font-done', fontDoneVis);
      step3El.classList.toggle('sub-preview-done', prevDoneVis);
      if (previewChip) previewChip.hidden = !prevDoneVis;
      if (textChip) {
        textChip.hidden = !textDoneVis;
        if (textChipVal && textarea) {
          var tcv = textarea.value.trim().replace(/\s+/g, ' ');
          textChipVal.textContent = tcv.length > 24 ? tcv.slice(0, 24) + '…' : tcv;
        }
      }
      if (fontChip) {
        fontChip.hidden = !fontDoneVis;
        if (fontChipVal && fontLabel) fontChipVal.textContent = fontLabel.textContent.trim();
      }
      // Confirm size & position: choreography only — visible with mode+content.
      if (sizeConfirm) sizeConfirm.hidden = !(curMode && step3Content());
      // Phase 5.3: the auto-grow is retired — any inline height the
      // configurator's keystroke autosize (or a degenerate hidden-state
      // measurement) wrote is cleared so the CSS one-line box governs.
      if (textarea && textarea.style.height) textarea.style.height = '';
      movePanel();
    }

    function goTo(n, scroll) {
      // Phase 1.5 Fix 4a: going back into the steps (dot click, chip tap,
      // Edit design, gate jump — every path routes through goTo) hides the
      // finale; only Finish re-shows it.
      if (typeof finale !== 'undefined' && finale && !finale.hidden) {
        exitFinale(false);
      }
      flags().cur = n;
      renderAll();
      if (scroll) {
        // Phase 3 Task 1b: if a text field (the note, typically) still holds
        // focus at advance time, the mobile keyboard is open — its dismissal
        // resizes the visual viewport UNDER the scroll loop and the landing
        // computes against a shrinking window. Blur first, give the UA one
        // frame to settle the viewport, then scroll to the step top.
        // Phase 5.1 Fix 2: entering step 4 anchors the product-info root
        // (title + price + case tabs at top, rows below, COMPLETE THE PAIR
        // in view). Fallback: the step stack top if the wrapper is absent.
        // Start Over YES routes to goTo(1) — per-step anchor, excluded here.
        var anchor = (n === 4) ? (root.closest('.product-info') ?? steps['1']) : steps[String(n)];
        if (isTypingTarget()) {
          try { document.activeElement.blur(); } catch (e) {}
          requestAnimationFrame(function () { settledScroll(anchor); });
        } else {
          settledScroll(anchor);
        }
      }
    }

    // --- CONTINUE ----------------------------------------------------------
    // Phase 2.6: inline vs-warn on failed gates (additive visual language —
    // Continue still refuses to advance). Font-required detection reuses the
    // configurator's own mechanics: the decoy "Choose your font" placeholder
    // class it maintains and whose absence its ATC font-gate keys on.
    function onContinue(stepN) {
      if (stepN === 3) {
        var mode = activeMode();
        if (mode === 'Text') {
          if (!(textarea && textarea.value.trim())) {
            subTextDone = false; // warn overrides chip collapse
            renderAll();
            warn(textWrapper);
            settledScroll(textWrapper);
            if (textarea) { try { textarea.focus(); } catch (e) {} }
            return;
          }
          if (fontIsPlaceholder()) {
            subFontDone = false; // warn overrides chip collapse
            renderAll();
            warn(fontTrigger);
            settledScroll(fontDropdown);
            return;
          }
        } else if (mode === 'Image') {
          if (!thumbVisible()) {
            warn(uploadBtn);
            settledScroll(uploadBtn);
            if (uploadBtn) { try { uploadBtn.focus(); } catch (e2) {} }
            return;
          }
        }
      }
      // CONTINUE on Step 1/2 confirms the shown default as the value.
      flags().confirmed[stepN] = true;
      captureCaseSnapshot(); // Phase B trigger 1: capture the active case on step confirm
      captureCasePreview(); // B.4-3: order-preview composite (Text cases only; no-op otherwise)
      // Phase 2.5: CONTINUE always advances to n+1 — sequential walk even
      // through already-done steps (their confirmed flags are never reset).
      goTo(Math.min(stepN + 1, 4), true);
    }

    // --- Delegated interactions — CAPTURE phase (root-cause fix) ----------
    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-cfg-finish]')) {
        onFinish();
        return;
      }
      var cont = e.target.closest('[data-cfg-continue]');
      if (cont) {
        var contStep = cont.closest('.cfg-step');
        if (contStep) onContinue(parseInt(contStep.getAttribute('data-cfg-step'), 10));
        return;
      }
      if (nonePill && e.target.closest('[data-cfg-none]')) {
        // Dispatch a REAL click on the active configurator pill so
        // configurator.js runs its own toggle-off; None then reflects state.
        var act = root.querySelector('.cfg-pill.is-active');
        if (act) act.click();
        setTimeout(renderAll, 0);
        return;
      }
      // Phase 2.9: chip tap re-expands its field for editing (re-arms the
      // choreography; re-collapse follows the same commit rules).
      var chip = e.target.closest('[data-cfg-subchip]');
      if (chip) {
        var chipKind = chip.getAttribute('data-cfg-subchip');
        if (chipKind === 'text') {
          subTextDone = false;
          choreoTextArmed = true;
          renderAll();
          settledScroll(textWrapper, { ifNeeded: true });
          if (textarea) { try { textarea.focus(); } catch (err) {} }
        } else if (chipKind === 'preview') {
          // Phase 3.1 re-open path: cancel a pending paced scroll, re-expand
          // the preview/sliders/confirm, scroll back to the preview area.
          subPreviewDone = false;
          cancelConfirmScroll();
          if (sizeConfirm) sizeConfirm.classList.remove('is-ticked');
          renderAll();
          // Phase 4 Task 3c: exactly one motion to the centered work area.
          settledScroll(previewArea, { align: 'center', fitBottom: sizeConfirm, ifNeeded: true });
        } else {
          subFontDone = false;
          renderAll();
          settledScroll(fontDropdown, { ifNeeded: true });
        }
        return;
      }
      // Confirm size & position — choreography only, never a gate.
      // Push 5 (audit §2): the scroll now starts on the SAME frame as the
      // collapse. The old transitionend+beat sequencing (Phase 3.5 Fix 3)
      // made the settle land ~700ms after the tap on phones — but it was
      // only ever compensating for the pre-settledScroll blind-delay era:
      // the settledScroll loop re-reads the target rect every frame, so a
      // scroll launched at t=0 bends with the 260ms collapse and lands on
      // the final offset in one motion. armConfirmScroll is cosmetic now
      // (tick-flash removal only).
      if (sizeConfirm && e.target.closest('[data-cfg-sizeconfirm]')) {
        if (flags().cur !== 3) return;
        sizeConfirm.classList.add('is-ticked');
        subPreviewDone = true;
        renderAll();
        armConfirmScroll(); // ✓ flash removal timer, decoupled from scrolling
        if (!isTypingTarget()) {
          settledScroll(steps['3'], { align: 'bottom', keepVisible: noteWrap });
        }
        return;
      }
      var header = e.target.closest('.cfg-step__header');
      if (header) {
        var stepEl = header.closest('.cfg-step');
        // Only done rows navigate (Task 1d: with scroll — they replaced the
        // rail dots); reopening never resets later completion.
        if (stepEl && stepEl.classList.contains('is-done')) {
          goTo(parseInt(stepEl.getAttribute('data-cfg-step'), 10), true);
        }
        return;
      }
      // Configurator-owned controls that change displayed values/state:
      // hard containment guard — a .cfg-dropdown__item only counts when it is
      // inside Step 1's stored model dropdown node, so font-dropdown items
      // (same class) can never affect Step 1.
      var ddItem = e.target.closest('.cfg-dropdown__item');
      if (ddItem && modelDropdown && modelDropdown.contains(ddItem)) {
        // Phase 2.6 model-change reset: capture phase runs BEFORE the
        // configurator's own handler, so the label still holds the OLD model.
        var oldModel = modelLabel ? modelLabel.textContent.trim() : '';
        var newModel = ddItem.getAttribute('data-model') ?? '';
        var hadContent = personalizationExists();
        setTimeout(function () {
          if (newModel && oldModel && newModel !== oldModel && hadContent) {
            resetPersonalization();
          }
          renderAll(); // read state AFTER configurator's handlers ran
        }, 0);
        return;
      }
      var modePill = e.target.closest('.cfg-pill');
      if (modePill) {
        // Phase 1.5 Fix 2: kill the one-frame flash. The configurator reveals
        // the mode panels SYNCHRONOUSLY in its own click handler, but the
        // flex-order mode class used to arrive a task later (setTimeout 0) —
        // one painted frame showed the panels in raw source order before the
        // reshuffle. Capture phase runs before configurator's handler AND
        // before paint, so pre-apply the PREDICTED mode class now (tapping
        // the active pill toggles off → null); renderAll still reconciles.
        var tapped = modePill.getAttribute('data-personalization');
        var predicted = modePill.classList.contains('is-active') ? null : tapped;
        var s3 = steps['3'];
        s3.classList.toggle('cfg-mode-text', predicted === 'Text');
        s3.classList.toggle('cfg-mode-image', predicted === 'Image');
        // Push 7 (phone QA overshoot report): SELECTING a mode — Text or
        // Image — anchors the STEP-3 CARD TOP under the sticky area (default
        // align 'top' = targetYOf + scrollMarginOf, the same mechanism every
        // goTo jump uses), so the card's top border stays visible with the
        // case preview beneath it. Push 6 centered the canvas row instead,
        // which overshot downward and hid the card top. Still unconditional
        // (no ifNeeded), same-frame per the Push-5 pattern (settledScroll's
        // per-frame re-read absorbs the panel reshuffle configurator does
        // right after this capture handler); deselect (predicted null) never
        // scrolls. The desktop-inertness gate stays on the CANVAS ROW's
        // renderedness, not the step card: steps['3'] renders at all widths,
        // but the behavior is mobile-only and the ≤768px-only canvas row is
        // the discriminator that actually distinguishes.
        if (predicted && flags().cur === 3 && !isTypingTarget() &&
            previewArea && previewArea.offsetParent !== null) {
          settledScroll(steps['3']);
        }
        setTimeout(renderAll, 0); // full reconcile AFTER configurator's handler
        return;
      }
      // --- Push 8: wallet (step 4) tap scrolls — phone QA. Mirrors the
      // Push-7 step-3 framing. SELECTING wallet Text/Image top-anchors the
      // step-4 card (same bare settledScroll 'top' mechanism — the card's
      // own scroll-margin-top clears the sticky area). DESELECTING — the
      // stepper None pill, or tapping the already-active pill (the
      // configurator's toggle-off) — bottom-anchors so the card's bottom
      // border sits fully visible at the viewport bottom. No keepVisible:
      // full bottom visibility wins over any competing element. The wallet
      // deselect "collapse" is instant [hidden]-attribute work (the 260ms
      // cfgFade animates EXPAND only), so the per-frame re-read lands on
      // settled geometry immediately.
      // e.isTrusted gate (mandatory): the Push-4 add-case reset
      // (resetFreshCaseWallet) and the walletNone listener both dispatch
      // SYNTHETIC clicks on these pills — those must never scroll (no
      // bouncing during the defensive reset). Real thumbs only.
      var wPillTap = e.target.closest('.cfg-wallet-pill[data-wallet-personalization]');
      var wNoneTap = e.target.closest('[data-cfg-wallet-none]');
      if (wPillTap || wNoneTap) {
        if (e.isTrusted === true && flags().cur === 4 && !isTypingTarget() &&
            walletToggle && walletToggle.checked &&
            walletCard && !walletCard.hidden &&
            previewArea && previewArea.offsetParent !== null) {
          // previewArea = the ≤768px-only canvas row — the same mobile
          // discriminator Push 6/7 use (step cards render at all widths).
          // Selection → top anchor; deselection → bottom anchor.
          var wSelecting = !!(wPillTap && !wPillTap.classList.contains('is-active'));
          if (wSelecting) settledScroll(steps['4']);
          else settledScroll(steps['4'], { align: 'bottom' });
        }
        return;
      }
      if (e.target.closest('.cfg-swatch') ||
          e.target.closest('.cfg-design-tile') ||
          e.target.closest('[data-cfg-remove-image]')) {
        setTimeout(renderAll, 0); // read state AFTER configurator's handlers ran
      }
    }, true); // CAPTURE — see header comment

    // --- Model-change reset (Phase 2.6) ------------------------------------
    // A model change with existing personalization resets the design to clean
    // via USER-PATH DISPATCH ONLY: real clicks on the configurator's own
    // controls (mode-pill toggle-off, its Remove-image button) plus a real
    // bubbling input event for the residual text — configurator state is
    // never touched directly. Active case only (all live state IS the active
    // case). Same-model reselection never resets (caller compares first).
    function personalizationExists() {
      return activeMode() != null ||
             !!(textarea && textarea.value.trim()) ||
             thumbVisible();
    }
    function resetPersonalization() {
      var act = root.querySelector('.cfg-pill.is-active');
      if (act) act.click(); // configurator's own toggle-off path
      if (textarea && textarea.value) {
        textarea.value = '';
        textarea.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (thumbVisible()) {
        var rm = root.querySelector('[data-cfg-remove-image]');
        if (rm) rm.click(); // configurator's own remove/clear control
      }
      clearWarn(textWrapper); clearWarn(fontTrigger); clearWarn(uploadBtn);
      resetSubFlow(); // chips + confirm tick reset with the design
    }

    // Keyboard paths (Enter selections in the dropdowns, pill activation).
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      // Keyboard model selection: same model-change reset as the click path —
      // capture the old label now, compare after configurator's handler ran.
      if (e.key === 'Enter' && modelDropdown && modelDropdown.contains(e.target)) {
        var oldModel = modelLabel ? modelLabel.textContent.trim() : '';
        var hadContent = personalizationExists();
        setTimeout(function () {
          var newModel = modelLabel ? modelLabel.textContent.trim() : '';
          if (newModel && oldModel && newModel !== oldModel && hadContent) {
            resetPersonalization();
          }
          renderAll();
        }, 0);
        return;
      }
      setTimeout(renderAll, 0);
    }, true); // CAPTURE

    // Live re-render while typing (value summary, None state, step-3 validity).
    // Typing also clears the empty-text warning and re-arms the commit
    // choreography for this entry.
    root.addEventListener('input', function (e) {
      if (e.target === textarea) {
        clearWarn(textWrapper);
        choreoTextArmed = true;
        renderAll();
      }
    });

    // --- Step-3 guided choreography (2.6, retargeted 2.9) ------------------
    // (a) Text commits on Enter/keyboard-done OR when a font gets selected —
    //     NEVER on blur alone (Phase 2.9). Commit latches the text chip and
    //     scrolls to the font field once per entry; typing re-arms.
    var choreoTextArmed = true;
    function onTextCommit() {
      if (flags().cur !== 3 || activeMode() !== 'Text') return;
      if (!(textarea && textarea.value.trim())) return;
      subTextDone = true;
      renderAll();
      if (choreoTextArmed) {
        choreoTextArmed = false;
        settledScroll(fontDropdown);
      }
    }
    if (textarea) {
      textarea.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') onTextCommit(); // observe only — never preventDefault
      });
    }
    // Phase 2.7 (3): when the font MENU opens (configurator toggles is-open on
    // the dropdown), keep it in view. Once per open. Read-only observation of
    // the class the configurator owns.
    // Phase 7 Item B: the original unconditional settledScroll(fontDropdown)
    // WAS the scroll bounce — top-aligning a mid-step element under the
    // sticky area is a +620px jump (measured) that floods step 4 + the ATC
    // region into the viewport. Retargeted to the OPEN MENU with ifNeeded
    // (≥70% visible → zero movement; the tapped trigger is virtually always
    // visible) and bottom-align for the genuinely-clipped case — scrolls
    // just enough that the list's bottom sits 16px above the viewport
    // bottom, keepVisible holding the trigger below the sticky area.
    if (fontDropdown) {
      var fontMenuWasOpen = false;
      var fontOpenObserver = new MutationObserver(function () {
        var open = fontDropdown.classList.contains('is-open');
        if (open && !fontMenuWasOpen) {
          var menuEl = fontDropdown.querySelector('.cfg-dropdown__menu') ?? fontDropdown;
          settledScroll(menuEl, { align: 'bottom', ifNeeded: true, keepVisible: fontDropdown });
        } else if (!open && fontMenuWasOpen) {
          // Menu closed while its reveal scroll may still be in flight — the
          // per-frame target re-read would now see a display:none menu (rect
          // all zeros → bottom-align resolves toward the page top). Kill it.
          cancelQueuedScroll();
        }
        fontMenuWasOpen = open;
      });
      fontOpenObserver.observe(fontDropdown, { attributes: true, attributeFilter: ['class'] });
    }
    // (b) Font selected → latch BOTH chips (a font selection also commits the
    //     text, per the 2.9 commit rules) and scroll to the preview +
    //     size/position area (retargeted from the note). Observed via the
    //     trigger label the configurator rewrites on selection; also clears
    //     the font warning. Skipped while the client is typing somewhere.
    if (fontLabel) {
      var fontLabelObserver = new MutationObserver(function () {
        clearWarn(fontTrigger);
        if (fontIsPlaceholder()) return;
        // Phase 3.1: a font change AFTER confirming size/position re-opens
        // the preview — the content it frames just changed.
        if (subPreviewDone) {
          subPreviewDone = false;
          cancelConfirmScroll();
        }
        if (activeMode() === 'Text') {
          subFontDone = true;
          if (textarea && textarea.value.trim()) subTextDone = true;
          renderAll();
        }
        // Phase 5 Fix 2: picking a font moves the viewport ZERO pixels — the
        // font-pick scroll is deleted (upload-completion scroll unaffected).
      });
      fontLabelObserver.observe(fontLabel, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    // Phase 4 Task 1d: keyboard activation for the tappable done-row headers
    // (they replaced the rail dots as the jump mechanism).
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var hdr = e.target.closest ? e.target.closest('.cfg-step.is-done > .cfg-step__header') : null;
      if (hdr) {
        e.preventDefault();
        goTo(parseInt(hdr.parentElement.getAttribute('data-cfg-step'), 10), true);
      }
    });

    // --- Error safety: a collapsed step must never trap an error ----------
    function expandContaining(el) {
      var stepEl = el && el.closest ? el.closest('.cfg-step') : null;
      if (stepEl && stepEl.classList.contains('is-collapsed')) {
        goTo(parseInt(stepEl.getAttribute('data-cfg-step'), 10), false);
      }
    }
    root.addEventListener('focusin', function (e) { expandContaining(e.target); });

    var errorTargets = [];
    // Wallet targets included: the wallet card lives inside collapsible Step 4
    // (Phase 2.3), and its ATC gates must never fire out of sight.
    ['.cfg-error-banner', '.cfg-text-error', '.cfg-font-error',
     '.cfg-upload__btn', '.cfg-textarea',
     '.cfg-font-dropdown .cfg-dropdown__trigger',
     '.cfg-wallet-text-error', '.cfg-wallet-upload-error',
     '.cfg-wallet-textinput', '.cfg-wallet-upload-btn'].forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) { errorTargets.push(el); });
    });
    function signaling(el) {
      if (el.classList && el.classList.contains('cfg-input--error')) return true;
      if (el.hasAttribute('hidden')) return false;
      if (el.classList && (el.classList.contains('cfg-error-msg') || el.classList.contains('cfg-error-banner'))) {
        return el.style.display !== 'none' && !el.hasAttribute('hidden');
      }
      if (el.classList && (el.classList.contains('cfg-wallet-text-error') || el.classList.contains('cfg-wallet-upload-error'))) {
        return true; // hidden-attr case already returned above — unhidden = visible error
      }
      return false;
    }
    var errObserver = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var el = muts[i].target;
        if (signaling(el)) {
          // A gate firing shortly after the finale ATC click means
          // configurator.js refused the submit — hide the finale, expand the
          // offending step with vs-warn, and settled-scroll to it (2.8).
          if (finaleAwaitingGate && (Date.now() - finaleAwaitingGate) < 5000 &&
              finale && !finale.hidden) {
            exitFinale(false);
            var gateStep = el.closest ? el.closest('.cfg-step') : null;
            if (gateStep) {
              gateStep.classList.add('vs-warn');
              goTo(parseInt(gateStep.getAttribute('data-cfg-step'), 10), true);
              continue;
            }
          }
          expandContaining(el);
        }
      }
    });
    errorTargets.forEach(function (el) {
      errObserver.observe(el, { attributes: true, attributeFilter: ['hidden', 'style', 'class'] });
    });
    // Phase 3.1 (Task 3): the thumb unhides on RAW upload success, before the
    // Cloudinary background-removal finishes — the engraving render lands
    // when the configurator removes is-processing from .cfg-mobile-preview.
    // Scroll on THAT signal; the thumb observer only clears errors, latches
    // state, and arms an 800ms fallback for any path that skips processing.
    // One scroll per upload cycle, never both.
    // RENAME-FRAGILITY NOTE: .cfg-mobile-preview and is-processing are
    // configurator-owned names.
    var mobilePreviewWrap = root.querySelector('.cfg-mobile-preview');
    var uploadScrollArmed = false; // a processing pass started this cycle
    var uploadScrolled = true;     // per-cycle latch (true = nothing pending)
    var uploadFallbackTimer = null;
    function fireUploadScroll() {
      if (uploadScrolled) return;
      uploadScrolled = true;
      if (uploadFallbackTimer) { clearTimeout(uploadFallbackTimer); uploadFallbackTimer = null; }
      if (flags().cur === 3 && activeMode() === 'Image' && !isTypingTarget()) {
        // Fix 2: same centered work-area landing as the font-pick scroll.
        // Push 5 (audit §3): ifNeeded gate removed — on phones the canvas row
        // was routinely ≥70% visible so the gate swallowed the scroll; the
        // completion scroll is now unconditional by design.
        settledScroll(previewArea, { align: 'center', fitBottom: sizeConfirm });
      }
    }
    if (mobilePreviewWrap) {
      var wasProcessing = mobilePreviewWrap.classList.contains('is-processing');
      var processingObserver = new MutationObserver(function () {
        var nowProcessing = mobilePreviewWrap.classList.contains('is-processing');
        if (nowProcessing && !wasProcessing) {
          // Processing started — this cycle scrolls on completion, not on the
          // thumb fallback.
          uploadScrolled = false;
          uploadScrollArmed = true;
          if (uploadFallbackTimer) { clearTimeout(uploadFallbackTimer); uploadFallbackTimer = null; }
        } else if (!nowProcessing && wasProcessing && uploadScrollArmed) {
          uploadScrollArmed = false;
          fireUploadScroll();
        }
        wasProcessing = nowProcessing;
      });
      processingObserver.observe(mobilePreviewWrap, { attributes: true, attributeFilter: ['class'] });
    }
    // --- Push 16: engrave-tips auto-hide ----------------------------------
    // The tips block (.cfg-engrave-tips, Liquid-gated by engrave_tips_enabled)
    // collapses while the active case already HAS an uploaded image and
    // expands when it doesn't. Signal = thumbVisible() — the same
    // .cfg-upload__thumb hidden-attr read the whole stepper trusts — fed by
    // the EXISTING thumbObserver below (extended, not a second observer on
    // the same node). Upload success, Remove, case-switch restores, and the
    // add-case reset all flow through that one attr; a boot call covers
    // restored initial state; Replace flips nothing (already suppressed —
    // correct). Collapse = collapseStep's measured technique: inline
    // scrollHeight px → class 0!important, so the 260ms animation always
    // runs real pixels. The block is queried LAZILY: it may not exist
    // (setting shipped dark) or may arrive with a Customizer refresh.
    var tipsClearTimer = null;
    function syncEngraveTips() {
      // Image-guidance note (2026-07-28): the relocated .cfg-engrave-note
      // (a .cfg-section sibling after the canvas row) carries ONLY the
      // has-image signal via .vs-note-on — the same thumbVisible() read.
      // CSS supplies the rest of the state matrix: .cfg-mode-image gates the
      // mode (class this file already sets in renderAll), and the
      // sub-preview-done hide list collapses it with the preview. Toggled
      // BEFORE the tips early-returns so it works when the tips block ships
      // dark (engrave_tips_enabled off) or the states already agree.
      var note = root.querySelector('.cfg-section > .cfg-engrave-note');
      if (note) note.classList.toggle('vs-note-on', thumbVisible());
      var tips = root.querySelector('.cfg-engrave-tips');
      if (!tips) return;
      var suppress = thumbVisible();
      if (suppress === tips.classList.contains('is-suppressed')) return;
      if (tipsClearTimer) { clearTimeout(tipsClearTimer); tipsClearTimer = null; }
      if (prefersReduced) {
        tips.classList.toggle('is-suppressed', suppress);
        tips.style.maxHeight = '';
        return;
      }
      if (suppress) {
        tips.style.maxHeight = tips.scrollHeight + 'px';
        void tips.offsetHeight; // commit the start height so the →0 transition runs
        tips.classList.add('is-suppressed');
      } else {
        tips.style.maxHeight = tips.scrollHeight + 'px'; // scrollHeight reads full content even while capped
        tips.classList.remove('is-suppressed');
        tipsClearTimer = setTimeout(function () {
          tips.style.maxHeight = ''; // uncap once settled — content may reflow later
          tipsClearTimer = null;
        }, 300);
      }
    }
    syncEngraveTips(); // boot: configurator-restored images get the correct initial state

    if (uploadThumb) {
      var thumbObserver = new MutationObserver(function () {
        if (thumbVisible()) {
          clearWarn(uploadBtn);
          // New upload cycle: open the latch and arm the no-processing
          // fallback. If is-processing appears within 800ms the observer
          // above takes over (and cancels this timer).
          uploadScrolled = false;
          if (uploadFallbackTimer) clearTimeout(uploadFallbackTimer);
          uploadFallbackTimer = setTimeout(function () {
            uploadFallbackTimer = null;
            var stillProcessing = mobilePreviewWrap && mobilePreviewWrap.classList.contains('is-processing');
            if (!uploadScrollArmed && !stillProcessing) fireUploadScroll();
          }, 800);
        }
        syncEngraveTips(); // Push 16: hide on the RAW-success unhide (before the
                           // completion scroll fires on is-processing removal),
                           // show again the moment Remove/case-switch hides it
        renderAll();
      });
      thumbObserver.observe(uploadThumb, { attributes: true, attributeFilter: ['hidden'] });
    }

    // --- Wallet pills (Phase 2: Made to Match) ------------------------------
    // configurator.js signals wallet-pill selection by toggling the is-active
    // class + aria-pressed on .cfg-wallet-pill (selectWalletPersonalization).
    // Observation + hidden-attr toggles only: the sublabel spans mirror the
    // case pills (--inactive vs --active; Liquid raw-EUR money inside, never
    // written here). The stepper-owned None pill is active when NO wallet
    // mode is — null = wallet in cart, no personalization, no fee, the
    // configurator's existing pill-deselect state.
    var walletPills = root.querySelectorAll('.cfg-wallet-pill[data-wallet-personalization]');
    var walletNone  = root.querySelector('[data-cfg-wallet-none]');
    function syncWalletPills() {
      var act = root.querySelector('.cfg-wallet-pill.is-active');
      var mode = act ? act.getAttribute('data-wallet-personalization') : null;
      walletPills.forEach(function (p) {
        var on = p.classList.contains('is-active');
        var ia = p.querySelector('.cfg-pill__price--inactive');
        var aa = p.querySelector('.cfg-pill__price--active');
        if (ia) ia.hidden = on;
        if (aa) aa.hidden = !on;
      });
      if (walletNone) {
        var noneOn = (mode == null);
        walletNone.classList.toggle('is-active', noneOn);
        walletNone.setAttribute('aria-pressed', noneOn ? 'true' : 'false');
      }
    }
    if (walletPills.length) {
      var walletPillObserver = new MutationObserver(syncWalletPills);
      walletPills.forEach(function (p) {
        walletPillObserver.observe(p, { attributes: true, attributeFilter: ['class', 'aria-pressed'] });
      });
      syncWalletPills();
    }
    // Wallet None click: with a wallet mode active, dispatch a REAL click on
    // that pill so configurator.js runs its own toggle-off (the stepper never
    // invents cart logic); None then reflects state via the observer above.
    if (walletNone) {
      walletNone.addEventListener('click', function () {
        var act = root.querySelector('.cfg-wallet-pill.is-active');
        if (act) act.click();
        setTimeout(syncWalletPills, 0);
      });
    }

    // --- Step-4 header toggle (Phase 3 Task 2) -----------------------------
    // The REAL .cfg-wallet-switch wrapper is RELOCATED into the step-4
    // label-row slot — never a proxy checkbox, never a dispatched click.
    // Binding style verified: configurator.js attaches its change listener
    // ELEMENT-BOUND (wToggle.addEventListener on the node resolved once by
    // getElementById) — element-bound listeners travel with the node across
    // DOM moves, so the handler keeps firing after relocation. Nothing is
    // delegated from the card container.
    // Multi-case finding: there is ONE wallet card per block shared by every
    // case — the configurator swaps per-case state onto it and restores the
    // toggle's .checked by ID — so the relocation is one-time + idempotent;
    // no per-case node swapping exists to do. The slot mirrors the card's
    // hidden state (no wallet match for the color → no toggle).
    var toggleSlot   = root.querySelector('[data-cfg-wallet-toggle-slot]');
    var walletCard   = root.querySelector('.cfg-wallet-card');
    var walletSwitch = walletCard ? walletCard.querySelector('.cfg-wallet-switch') : null;
    function placeWalletToggle() {
      if (!toggleSlot || !walletSwitch) return;
      if (walletSwitch.parentElement !== toggleSlot) toggleSlot.appendChild(walletSwitch);
      toggleSlot.hidden = !!(walletCard && walletCard.hidden);
    }
    if (walletCard && toggleSlot) {
      var walletCardObserver = new MutationObserver(placeWalletToggle);
      walletCardObserver.observe(walletCard, { attributes: true, attributeFilter: ['hidden'] });
      placeWalletToggle();
    }

    // --- Per-case sync -----------------------------------------------------
    function syncActiveCase() {
      var activeTab = casesEl ? casesEl.querySelector('.cfg-case-tab.is-active') : null;
      activeCase = activeTab ? (parseInt(activeTab.getAttribute('data-cfg-case-index'), 10) || 0) : 0;
    }
    // --- Push 4 Task 2: defensive wallet reset on ADD-CASE only -----------
    // Against the audited configurator this is a NO-OP: addCase() pushes
    // makeBlankDesign() and loadDesign() resets both wallet state and pill
    // UI. It guards the regression class where a freshly added case inherits
    // wallet personalization internally while the pills render untouched —
    // the charged-but-never-displayed fee. Real user-path dispatch only
    // (configurator state is never written directly). The pill toggle is
    // STATE-relative, so the sequence self-resyncs from ANY start state:
    //   1. click the Text pill — configurator re-syncs pill DOM to real state
    //   2. click the now-lit .is-active pill (if any) — nets mode = null
    //   3. wallet switch rendering checked → one real click (its change
    //      handler copies DOM → state, deterministic after a single click)
    //   4. clear the wallet text input via a real bubbling input event
    //      (resetPersonalization pattern) so no fee-able content lingers.
    function resetFreshCaseWallet() {
      var textPill = root.querySelector('.cfg-wallet-pill[data-wallet-personalization="Text"]');
      if (!textPill) return; // no wallet card for this product — nothing to reset
      textPill.click();
      var act = root.querySelector('.cfg-wallet-pill.is-active');
      if (act) act.click();
      // walletToggle resolves at init (finale block below); assigned long
      // before any click can land here.
      if (walletToggle && walletToggle.checked) walletToggle.click();
      var wText = root.querySelector('.cfg-wallet-textinput');
      if (wText && wText.value) {
        wText.value = '';
        wText.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    // Phase B trigger 2: switch-away flush in CAPTURE phase. configurator.js's
    // own tab handler switches the case on the BUBBLE phase (it runs before the
    // stepper's bubble handler, so the DOM has already flipped by then — a
    // bubble-phase flush would capture the incoming case). A capture-phase
    // listener fires on the way DOWN to the tab, before that handler, so
    // activeCase and the live DOM still reflect the OUTGOING case. Read-only.
    if (casesEl) {
      casesEl.addEventListener('click', function (e) {
        if (!e.target || !e.target.closest) return;
        if (e.target.closest('[data-cfg-select]') || e.target.closest('[data-cfg-add-case]')) {
          captureCaseSnapshot(activeCase); // outgoing case — DOM still old in capture phase
          captureCasePreview(activeCase);  // B.4-3: outgoing case's order-preview composite
        }
      }, true);
    }
    if (casesEl) {
      casesEl.addEventListener('click', function (e) {
        // Phase 8 Push 3 (Task 3): ADDING a case while the finale is open is
        // a departure from the reviewed order — exit the finale (same path
        // as a step-square interaction). Case SWITCHING keeps the Push-2
        // stay-open + refresh behavior below.
        // Push 4: wasAdd is captured NOW (same tick as the click) because the
        // deferred callback below also fires for tab-select and remove clicks
        // — the wallet reset must never run on those.
        var wasAdd = !!(e.target.closest && e.target.closest('[data-cfg-add-case]'));
        if (wasAdd) {
          if (typeof finale !== 'undefined' && finale && !finale.hidden) exitFinale(false);
        }
        setTimeout(function () {
          syncActiveCase();
          if (wasAdd) resetFreshCaseWallet(); // Push 4 Task 2 — add-case only
          resetSubFlow(); // chip states never carry across cases
          renderAll(); // this case's own flags decide cur/done — nothing carries over
          // Phase 8 Push 2 (Item-2 staleness fix, approved candidate 1): a
          // case switch while the finale is open re-reads every finale
          // surface — wallet source/flags, captions, prices, total —
          // from the NEWLY active case's live state. Without this only the
          // rAF case mirror tracked the switch (gray case / turquoise
          // wallet). setTimeout(0) already sequences us after the
          // configurator's own switch work, so colorNameEl is fresh.
          if (finale && !finale.hidden) { updateFinaleContent(); buildFinaleRows(); }
          refreshAtcCount(); // Phase 8 Push 3 (Task 1): "— N cases" label
        }, 0);
      });
    }
    if (removeModal) {
      removeModal.addEventListener('click', function (e) {
        if (e.target.closest('[data-cfg-modal-confirm]')) {
          // 2026-07-28 fix: SHIFT the per-case stores past the removed index
          // instead of wiping them — surviving cases keep step progress,
          // snapshots, previews and wallet-font state. All four maps are
          // handled HERE (walletFontPicked's own confirm handler below only
          // resyncs its widget) so the pending index is consumed exactly once.
          reindexCaseMap(caseFlags, pendingRemoveIdx);
          reindexCaseMap(caseSnapshots, pendingRemoveIdx);
          reindexCaseMap(casePreviews, pendingRemoveIdx);
          reindexCaseMap(walletFontPicked, pendingRemoveIdx);
          pendingRemoveIdx = -1;
          setTimeout(function () {
            syncActiveCase(); resetSubFlow(); renderAll();
            // Phase 8 Push 2: removal switches the active case too (the only
            // other case-switch path) — same finale refresh.
            if (finale && !finale.hidden) { updateFinaleContent(); buildFinaleRows(); }
            refreshAtcCount(); // Phase 8 Push 3 (Task 1)
          }, 200);
        }
      });
    }

    // --- Preview mirror (rAF drawImage; <1000px + visible only) -----------
    var mqMobile = window.matchMedia('(max-width: 999px)');
    var mirrorCtx = (mirrorCanvas && mirrorCanvas.getContext) ? mirrorCanvas.getContext('2d') : null;
    var rafId = null;
    function mirrorTick() {
      rafId = null;
      if (document.hidden || !mqMobile.matches) return; // paused — resumed below
      if (mirrorCtx && sourceCanvas && sourceCanvas.width > 0 && sourceCanvas.height > 0) {
        var W = mirrorCanvas.width, H = mirrorCanvas.height;
        var s = Math.min(W / sourceCanvas.width, H / sourceCanvas.height);
        var dW = sourceCanvas.width * s, dH = sourceCanvas.height * s;
        mirrorCtx.clearRect(0, 0, W, H);
        try {
          mirrorCtx.drawImage(sourceCanvas, (W - dW) / 2, (H - dH) / 2, dW, dH);
        } catch (e) { /* source mid-resize — retry next frame */ }
      }
      rafId = requestAnimationFrame(mirrorTick);
    }
    function startMirror() {
      if (rafId == null && !document.hidden && mqMobile.matches) {
        rafId = requestAnimationFrame(mirrorTick);
      }
    }
    document.addEventListener('visibilitychange', startMirror);
    if (mqMobile.addEventListener) mqMobile.addEventListener('change', startMirror);
    else if (mqMobile.addListener) mqMobile.addListener(startMirror);

    // --- FINALE (Phase 2.7): Finish → bundle reveal → real ATC -------------
    var BLOCK_ID = root.id.replace('configurator-', '');
    var finale           = root.querySelector('[data-cfg-finale]');
    var finaleCase       = finale ? finale.querySelector('[data-cfg-finale-case]') : null;
    var finaleWalletWrap = finale ? finale.querySelector('[data-cfg-finale-wallet-wrap]') : null;
    var finaleWallet     = finale ? finale.querySelector('[data-cfg-finale-wallet]') : null;
    var finaleWalletImg  = finale ? finale.querySelector('[data-cfg-finale-wallet-img]') : null;
    var finaleEdit       = finale ? finale.querySelector('[data-cfg-finale-edit]') : null;
    var finishBtn        = root.querySelector('[data-cfg-finish]');
    var walletToggle     = document.getElementById('cfg-wallet-toggle-' + BLOCK_ID);
    var walletPreviewCanvas = root.querySelector('[data-cfg-wallet-preview]');
    var finaleRafId = null;
    var finaleAwaitingGate = 0;

    // =====================================================================
    // Phase B: per-case snapshot harness (INVISIBLE TO CUSTOMERS)
    // ---------------------------------------------------------------------
    // Read-only DOM capture — no writes, no configurator state access, no
    // dispatched clicks, no loadDesign. Records each configured case's
    // last-known config + a downscaled art thumbnail so a future multi-case
    // finale (Phase C+) can render every case. Nothing here renders anything
    // or alters the live preview, the current finale, ATC, or the cart POST.
    //
    // Art is drawn from the configurator's own active-case canvas
    // (sourceCanvas = [data-configurator-mobile-preview] — the same source the
    // finale mirror uses) into a small offscreen canvas BEFORE encoding, so
    // toDataURL stays cheap (rows need only ~30px-wide art) and memory tiny.
    // Every DOM read is guarded; a missing element yields '' / null, never a
    // throw. toDataURL is wrapped so a mid-resize/security error can't break
    // the configurator.
    var SNAP_MAX = 5;               // matches configurator MAX_CASES; overwrite by index
    // Push B Step 1: crop to the case art, then encode at 224px wide (long edge
    // capped at 448) as WebP q0.9. The old path contain-fitted the whole 2160²
    // frame into 128², so 63% of the stored pixels were white margin and the art
    // was only ~47px wide — it upscaled ~3.6x into a 56px thumb at DPR 3.
    var SNAP_W = 224;
    var SNAP_MAX_LONG = 448;
    var snapThumb = null, snapThumbCtx = null;
    var snapMime = null;            // feature-tested once: 'image/webp' | 'image/png'
    // Content-bbox cache. Key is model|colour — Step 0 measured the silhouette to
    // vary by BOTH (x 12px, y 30px, w 18px, h 36px within one model; 38px of height
    // between models for the same colour), because each colour is its own source
    // photo. The size slider was verified NOT to move the silhouette, so it is
    // deliberately absent from the key. Engraving was verified not to contaminate
    // the scan: configurator hard-clamps the artwork inside the case-body travel
    // zone (configurator.js:631-638), so text at any size and a 1600x400 image at
    // every slider extreme all left the bbox byte-identical.
    var snapBBoxCache = {};
    function detectSnapMime() {
      if (snapMime) return snapMime;
      snapMime = 'image/png';
      try {
        var c = document.createElement('canvas');
        c.width = 2; c.height = 2;
        // feature-test the RETURNED mime, never the UA
        if (c.toDataURL('image/webp').indexOf('data:image/webp') === 0) snapMime = 'image/webp';
      } catch (e) { snapMime = 'image/png'; }
      return snapMime;
    }
    function caseArtBBox() {
      if (!sourceCanvas || !sourceCanvas.width || !sourceCanvas.height) return null;
      // Painted check — same precedent as snapshotWalletThumb (size gate at
      // 1423 + centre-pixel alpha probe at 1427-1428). Until configurator's
      // first variant-image render lands, this canvas is still its HTML
      // default 300x150 and fully transparent; the white test below reads
      // transparent black (0,0,0,0) as content, so EVERY pixel qualifies and
      // the bbox comes back as the whole frame. Placed BEFORE the cache read
      // as well as the scan, so an unpainted canvas neither reads a stale
      // bbox nor writes a bad one — snapBBoxCache stays empty for this
      // model|colour and the next capture re-measures.
      // Threshold 600 is the wallet's own value, transferred verbatim: it
      // clears the 300px unpainted default by 2x while sitting far below any
      // real product photo (the live case canvas is 2160). Deliberately NOT
      // 2160 — the canvas is sized from sourceImage.naturalWidth, which
      // varies per variant photo, so a 2160 gate would reject legitimately
      // smaller art. Probe kept in its own try: getImageData throws on a
      // tainted canvas, and this runs outside the scan's try below.
      if (sourceCanvas.width < 600) return null;
      try {
        var probe = sourceCanvas.getContext('2d').getImageData(Math.floor(sourceCanvas.width * 0.5), Math.floor(sourceCanvas.height * 0.5), 1, 1).data;
        if (probe[3] < 8) return null;
      } catch (e) { return null; }
      var key = (valueFor(1) || '?') + '|' + (colorNameEl ? colorNameEl.textContent.trim() : '?');
      if (snapBBoxCache[key]) return snapBBoxCache[key];
      try {
        var W = sourceCanvas.width, H = sourceCanvas.height;
        var d = sourceCanvas.getContext('2d').getImageData(0, 0, W, H).data;
        var minX = W, minY = H, maxX = -1, maxY = -1;
        for (var y = 0; y < H; y += 2) {
          for (var x = 0; x < W; x += 2) {
            var i = (y * W + x) * 4;
            if (Math.abs(d[i] - 255) > 12 || Math.abs(d[i + 1] - 255) > 12 || Math.abs(d[i + 2] - 255) > 12) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX < 0) return null;
        var b = { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
        snapBBoxCache[key] = b;
        return b;
      } catch (e) { return null; }
    }
    // Returns { url, aspect }. aspect is the CROPPED art's w/h — stored so a row can
    // reason about it; the row still renders into a FIXED 1:2.00 box with
    // object-fit:contain (Amendment 2) so every row is exactly the same height.
    function snapshotThumb() {
      if (!sourceCanvas || !sourceCanvas.width || !sourceCanvas.height) return { url: '', aspect: 0 };
      // Painted check — mirrors snapshotWalletThumb 1423/1428, and must live
      // HERE too: a null bbox is not a skip signal for this function, it is
      // the "use the whole canvas" fallback on the next two lines. Without
      // this gate an unpainted canvas still encodes — a fully transparent
      // thumb at aspect 2.0 — and captureCaseSnapshot stores it over a good
      // one. Returning the existing sentinel makes an unpainted canvas
      // produce NO thumbnail, exactly as the wallet path does.
      // The b ? ... : ... fallback below is deliberately left intact: a null
      // bbox from a genuinely PAINTED canvas keeps today's behaviour. This
      // gate is about paintedness, not about b.
      if (sourceCanvas.width < 600) return { url: '', aspect: 0 };
      try {
        var probe = sourceCanvas.getContext('2d').getImageData(Math.floor(sourceCanvas.width * 0.5), Math.floor(sourceCanvas.height * 0.5), 1, 1).data;
        if (probe[3] < 8) return { url: '', aspect: 0 };
      } catch (e) { return { url: '', aspect: 0 }; }
      var b = caseArtBBox();
      var sx = b ? b.x : 0, sy = b ? b.y : 0;
      var sw = b ? b.w : sourceCanvas.width, sh = b ? b.h : sourceCanvas.height;
      var tw = SNAP_W, th = Math.round(tw * sh / sw);
      if (th > SNAP_MAX_LONG) { th = SNAP_MAX_LONG; tw = Math.round(th * sw / sh); }
      try {
        if (!snapThumb) {
          snapThumb = document.createElement('canvas');
          snapThumbCtx = snapThumb.getContext ? snapThumb.getContext('2d') : null;
        }
        if (!snapThumbCtx) return { url: '', aspect: 0 };
        snapThumb.width = tw; snapThumb.height = th;
        snapThumbCtx.clearRect(0, 0, tw, th);
        snapThumbCtx.drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, tw, th);
        var mime = detectSnapMime();
        var url = (mime === 'image/webp') ? snapThumb.toDataURL('image/webp', 0.9) : snapThumb.toDataURL('image/png');
        return { url: url, aspect: +(sw / sh).toFixed(4) };
      } catch (e) { return { url: '', aspect: 0 }; } // tainted / mid-resize — never throw
    }
    // B.3: per-case WALLET snapshot — only when that case has wallet
    // personalization (clean wallets keep the CDN image path). The wallet canvas
    // ([data-cfg-wallet-preview], 600x600 when painted) contain-fits the SAME
    // 800x800 CDN photo the rows crop, so the silhouette framing is the CDN's by
    // construction — crop by those FIXED percentages (measured across all 9
    // colours, spread <=0.5%): no runtime scan, no cache, and engraving text (its
    // area x20-80% can overhang the silhouette by <=2.85% per side) can never
    // move the frame. Same encode pipeline as the case (224 wide, WebP q0.9 /
    // PNG fallback).
    var WSNAP = { x: 0.2275, y: 0.115, w: 0.544, h: 0.771 };
    function snapshotWalletThumb() {
      var wc = walletPreviewCanvas;
      if (!wc || wc.width < 600 || !wc.height) return { url: '', aspect: 0 };
      try {
        // painted check: a pixel at the wallet's centre must be opaque (the
        // canvas is transparent until configurator's rAF render has run).
        var probe = wc.getContext('2d').getImageData(Math.floor(wc.width * 0.5), Math.floor(wc.height * 0.5), 1, 1).data;
        if (probe[3] < 8) return { url: '', aspect: 0 };
        var sx = wc.width * WSNAP.x, sy = wc.height * WSNAP.y;
        var sw = wc.width * WSNAP.w, sh = wc.height * WSNAP.h;
        var tw = SNAP_W, th = Math.round(tw * sh / sw);
        if (th > SNAP_MAX_LONG) { th = SNAP_MAX_LONG; tw = Math.round(th * sw / sh); }
        if (!snapThumb) {
          snapThumb = document.createElement('canvas');
          snapThumbCtx = snapThumb.getContext ? snapThumb.getContext('2d') : null;
        }
        if (!snapThumbCtx) return { url: '', aspect: 0 };
        snapThumb.width = tw; snapThumb.height = th;
        snapThumbCtx.clearRect(0, 0, tw, th);
        snapThumbCtx.drawImage(wc, sx, sy, sw, sh, 0, 0, tw, th);
        var mime = detectSnapMime();
        var url = (mime === 'image/webp') ? snapThumb.toDataURL('image/webp', 0.9) : snapThumb.toDataURL('image/png');
        return { url: url, aspect: +(sw / sh).toFixed(4) };
      } catch (e) { return { url: '', aspect: 0 }; }
    }
    function readSliderValue(sel) {
      var els = document.querySelectorAll(sel);
      var vis = null, first = null;
      Array.prototype.forEach.call(els, function (e) { if (!first) first = e; if (!vis && e.offsetParent) vis = e; });
      var el = vis ?? first;
      return el ? String(el.value) : '';
    }
    function captureCaseSnapshot(idx) {
      // idx defaults to the CURRENT active case. An explicit index is passed
      // only by the switch-away flush (still the outgoing case, DOM verified).
      if (idx == null) idx = activeCase;
      if (idx == null || idx < 0 || idx >= SNAP_MAX) return;
      var data = window['CFG_DATA_' + BLOCK_ID];
      var colorName = colorNameEl ? colorNameEl.textContent.trim() : '';
      var colorHex = (data && data.colorHexMap && colorName) ? (data.colorHexMap[colorName] ?? '') : '';
      var snapArt = snapshotThumb();
      // B.3: wallet art snapshot only when this case has an active wallet
      // personalization mode — clean wallets stay on the CDN path (url '').
      var wOnNow = !!(walletToggle && walletToggle.checked);
      var wModeNow = (function () { var p = root.querySelector('.cfg-wallet-pill.is-active'); return p ? p.getAttribute('data-wallet-personalization') : null; })();
      var walletSnapArt = (wOnNow && wModeNow) ? snapshotWalletThumb() : { url: '', aspect: 0 };
      caseSnapshots[idx] = {
        model: valueFor(1),                 // step-1 model label (guarded read)
        colorName: colorName,
        colorHex: colorHex,                 // CFG_DATA.colorHexMap (read-only config)
        mode: activeMode(),                 // 'Text' | 'Image' | null
        summary: valueFor(3),               // '“text…”' | 'Custom image' | 'Basic'
        walletOn: !!(walletToggle && walletToggle.checked),
        // wallet personalization mode ('Text' | 'Image' | null) — the active
        // wallet pill; drives the per-case wallet fee in the row breakdown.
        walletMode: (function () { var p = root.querySelector('.cfg-wallet-pill.is-active'); return p ? p.getAttribute('data-wallet-personalization') : null; })(),
        // B.4: wallet text presence — the Finish font-gate needs it for
        // NON-active cases (the shared canvas/state only shows the active one).
        walletHasText: (function () { var t = root.querySelector('.cfg-wallet-textinput'); return !!(t && t.value.trim()); })(),
        // B.5: image-chosen signal — the wallet upload thumb is unhidden by
        // configurator exactly when walletCustomImageReady (same pattern as the
        // case-side thumbVisible()). Needed for the G4 gate on non-active cases.
        walletHasImage: (function () { var th = root.querySelector('.cfg-wallet-upload-thumb'); return !!(th && !th.hidden); })(),
        // B.4-3: size/position values for the order-preview properties. The
        // size/position sliders exist twice (mobile inside the configurator,
        // desktop in .hero-desktop-row) — read the VISIBLE instance (the one
        // the customer actually used at this viewport), fall back to the first.
        sizeValue: readSliderValue('.cfg-size__slider'),
        positionValue: readSliderValue('.cfg-position__slider'),
        walletSizeValue: (function () { var s = root.querySelector('.cfg-wallet-size input'); return s ? String(s.value) : ''; })(),
        thumbDataURL: snapArt.url,
        thumbAspect: snapArt.aspect,     // cropped art w/h (Step 1)
        walletThumbDataURL: walletSnapArt.url,   // '' = clean wallet -> CDN path
        walletThumbAspect: walletSnapArt.aspect,
        capturedAt: Date.now()
      };
    }
    // ===================================================================
    // B.4-3: ORDER-PREVIEW composites for TEXT-mode cases (the one line type
    // shipping without a composited preview — Image cases and personalized
    // wallets already carry configurator's own _Order Preview, and those
    // paths are untouched). Captured at the CONFIRM / SWITCH-AWAY / FINISH
    // triggers only (never the 400ms debounce), from the live case canvas —
    // which at capture time holds the same composite renderCompositeInto
    // paints (that function is configurator-private; the canvas it feeds is
    // the readable equivalent). 1600px long edge, WebP q0.9 / PNG fallback.
    // Uploaded to Cloudinary at configure time, dirty-checked on the exact
    // dataURL string so an unchanged design never re-uploads; one silent
    // retry, then marked missing. A missing preview NEVER blocks checkout.
    // ===================================================================
    var casePreviews = {}; // idx -> { dataUrl, url, uploadedFor, pending }
    function captureCasePreview(idx) {
      if (idx == null) idx = activeCase;
      if (idx == null || idx < 0 || idx >= SNAP_MAX) return;
      try {
        var mode = activeMode();
        var hasText = !!(textarea && textarea.value.trim());
        if (mode !== 'Text' || !hasText) { delete casePreviews[idx]; return; }
        if (!sourceCanvas || !sourceCanvas.width || !sourceCanvas.height) return;
        var scale = 1600 / Math.max(sourceCanvas.width, sourceCanvas.height);
        var off = document.createElement('canvas');
        off.width = Math.round(sourceCanvas.width * scale);
        off.height = Math.round(sourceCanvas.height * scale);
        off.getContext('2d').drawImage(sourceCanvas, 0, 0, off.width, off.height);
        var mime = detectSnapMime();
        var dataUrl = (mime === 'image/webp') ? off.toDataURL('image/webp', 0.9) : off.toDataURL('image/png');
        var rec = casePreviews[idx] ?? (casePreviews[idx] = {});
        rec.dataUrl = dataUrl;
        uploadCasePreview(idx); // fire-and-forget; dirty-checked inside
      } catch (e) { /* preview capture must never throw into the flow */ }
    }
    function uploadCasePreview(idx) {
      var rec = casePreviews[idx];
      if (!rec || !rec.dataUrl) return Promise.resolve();
      if (rec.url && rec.uploadedFor === rec.dataUrl) return Promise.resolve(); // clean
      if (rec.pending) return rec.pending;
      var forDataUrl = rec.dataUrl;
      function attempt(n) {
        return fetch(forDataUrl)
          .then(function (r) { return r.blob(); })
          .then(function (blob) {
            var fd = new FormData();
            fd.append('file', blob, 'vs-order-preview.webp');
            fd.append('upload_preset', 'velvet_customize');
            return fetch('https://api.cloudinary.com/v1_1/debkkfqay/image/upload', { method: 'POST', body: fd });
          })
          .then(function (res) { return res.json(); })
          .then(function (j) {
            if (j && j.secure_url) { rec.url = j.secure_url; rec.uploadedFor = forDataUrl; }
            else throw new Error('upload returned no URL');
          })
          .catch(function (e) {
            if (n < 1) return attempt(n + 1); // one silent retry
            // marked missing — never surfaced to the customer
          });
      }
      rec.pending = attempt(0).then(function () { rec.pending = null; }, function () { rec.pending = null; });
      return rec.pending;
    }

    // Trigger 4: debounced re-capture whenever the active case's inputs/art
    // change — captures whatever is active AT FIRE TIME (self-consistent
    // DOM<->index), so a pending timer that survives a switch simply records
    // the new active case. Capture-phase, read-only, scoped to this block's
    // configurator subtree.
    var snapDebounceTimer = null;
    function scheduleSnapshot() {
      if (snapDebounceTimer) clearTimeout(snapDebounceTimer);
      snapDebounceTimer = setTimeout(function () {
        snapDebounceTimer = null;
        captureCaseSnapshot();
      }, 400);
    }
    function onCfgActivityForSnapshot(e) {
      var t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      // root === this block's #configurator-<id>; the steps, pills, textarea,
      // upload and the relocated wallet toggle all live inside it. Desktop
      // hero sliders are out of scope here (mobile-art path); confirm /
      // switch-away / Finish still cover any case configured via those.
      if (root.contains(t)) scheduleSnapshot();
    }
    document.addEventListener('input', onCfgActivityForSnapshot, true);
    document.addEventListener('change', onCfgActivityForSnapshot, true);
    document.addEventListener('pointerup', onCfgActivityForSnapshot, true);

    // Read-only verification/debug hook (INVISIBLE — renders nothing). Lets
    // Phase-B QA and later phases read the captured snapshots. Never mutates
    // configurator state; safe to keep or remove.
    try {
      window.__vsSnap = window.__vsSnap || {};
      window.__vsSnap[BLOCK_ID] = {
        snapshots: caseSnapshots,
        capture: function (i) { captureCaseSnapshot(i); return caseSnapshots; },
        previews: casePreviews
      };
    } catch (e) {}

    // ===================================================================
    // B.4-3: /cart/add.js AUGMENTATION WRAPPER — the checkout path, so
    // FAIL-OPEN is the contract: ANY exception anywhere in here forwards the
    // ORIGINAL request untouched. A lost preview is acceptable; a blocked
    // add-to-cart never is. Installed once (window.__vsCartWrap guard);
    // every non-matching fetch passes straight through.
    //
    // Correlation: buildCartItems (configurator.js:2627) iterates designs[]
    // IN ORDER and emits, per case: case line -> [upgrade] -> [wallet] ->
    // [wallet upgrade]. Case-variant ids (CFG_DATA.variants) and wallet ids
    // (walletMap) are disjoint sets, so classifying each line by id and
    // counting case lines maps line -> case index deterministically; a wallet
    // line always belongs to the case line that precedes it. This cannot
    // mismatch on multi-case orders because the emission order is the same
    // loop that built the payload.
    //
    // APPEND-ONLY: new keys are added only when absent — every pre-existing
    // property (incl. _Linked Group and configurator's own _Order Preview on
    // Image/wallet lines) passes through byte-identical.
    // ===================================================================
    if (!window.__vsCartWrap) {
      window.__vsCartWrap = true;
      var __vsOrigFetch = window.fetch;
      var __vsAddIfAbsent = function (props, k, v) {
        if (v != null && v !== '' && !(k in props)) props[k] = String(v);
      };
      var __vsAugmentItems = function (items) {
        var data = window['CFG_DATA_' + BLOCK_ID] || {};
        var caseIds = {}; (data.variants || []).forEach(function (v) { caseIds[Number(v.id)] = true; });
        var walletIds = {}; Object.keys(data.walletMap || {}).forEach(function (k) { var w = data.walletMap[k]; if (w && w.variantId != null) walletIds[Number(w.variantId)] = true; });
        var caseIdx = -1;
        return items.map(function (item) {
          var id = Number(item.id);
          if (caseIds[id]) {
            caseIdx++;
            var s = caseSnapshots[caseIdx] || {};
            var props = {}; var op = item.properties || {}; for (var k in op) props[k] = op[k];
            if (props['Personalization Type']) {
              __vsAddIfAbsent(props, '_Engraving Size', s.sizeValue);
              __vsAddIfAbsent(props, '_Engraving Position', s.positionValue);
              if (props['Personalization Type'] === 'Text') {
                var rec = casePreviews[caseIdx];
                if (rec && rec.url) __vsAddIfAbsent(props, '_Order Preview', rec.url);
              }
            }
            return { id: item.id, quantity: item.quantity, properties: props };
          }
          if (walletIds[id]) {
            var s2 = caseSnapshots[caseIdx] || {};
            var props2 = {}; var op2 = item.properties || {}; for (var k2 in op2) props2[k2] = op2[k2];
            if (props2['Personalization Type']) __vsAddIfAbsent(props2, '_Wallet Size', s2.walletSizeValue);
            return { id: item.id, quantity: item.quantity, properties: props2 };
          }
          return item; // upgrade / unknown lines pass through untouched
        });
      };
      window.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : ((input && input.url) || '');
        var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
        if (url.indexOf('/cart/add.js') < 0 || method !== 'POST') return __vsOrigFetch.apply(this, arguments);
        var self = this, args = arguments;
        return (function () {
          var restoreBusy = function () {};
          return Promise.resolve()
            .then(function () {
              if (window.__vsSnap && window.__vsSnap.__forcePreviewThrow) throw new Error('forced test throw');
              var bodyStr = (init && typeof init.body === 'string') ? init.body : null;
              if (!bodyStr) return __vsOrigFetch.apply(self, args);
              var payload = JSON.parse(bodyStr);
              if (!payload || !Array.isArray(payload.items)) return __vsOrigFetch.apply(self, args);
              // STRAGGLERS: kick uploads for Text case lines still missing a URL,
              // wait at most 4s TOTAL, then proceed regardless.
              var pend = [];
              try {
                var data = window['CFG_DATA_' + BLOCK_ID] || {};
                var caseIds = {}; (data.variants || []).forEach(function (v) { caseIds[Number(v.id)] = true; });
                var ci = -1;
                payload.items.forEach(function (it) {
                  if (!caseIds[Number(it.id)]) return;
                  ci++;
                  var p = it.properties || {};
                  if (p['Personalization Type'] === 'Text' && !(casePreviews[ci] && casePreviews[ci].url)) {
                    var pr = uploadCasePreview(ci);
                    if (pr && typeof pr.then === 'function') pend.push(pr);
                  }
                });
              } catch (e) { pend = []; }
              var afterWait = Promise.resolve();
              if (pend.length) {
                var atcBtn = document.querySelector('[data-cfg-finale-atc-slot] button');
                if (atcBtn) {
                  atcBtn.setAttribute('aria-busy', 'true'); // the theme button's own loading affordance
                  restoreBusy = function () { try { atcBtn.removeAttribute('aria-busy'); } catch (e) {} };
                }
                afterWait = Promise.race([
                  Promise.all(pend),
                  new Promise(function (r) { setTimeout(r, 4000); }) // hard cap — POST always proceeds
                ]);
              }
              return afterWait.then(function () {
                restoreBusy();
                var augmented = __vsAugmentItems(payload.items);
                if (window.__vsSnap) window.__vsSnap.__lastCartPayload = { items: augmented }; // debug/verification surface
                if (window.__vsSnap && window.__vsSnap.__dryRun) {
                  return new Response(JSON.stringify({ status: 422, message: 'vs-dryrun' }), { status: 422, headers: { 'Content-Type': 'application/json' } });
                }
                var newInit = {}; for (var ik in init) newInit[ik] = init[ik];
                newInit.body = JSON.stringify({ items: augmented });
                return __vsOrigFetch.call(self, input, newInit);
              });
            })
            .catch(function (e) {
              restoreBusy();
              try { console.warn('[vs-stepper] cart augmentation skipped (fail-open):', e && e.message); } catch (e2) {}
              return __vsOrigFetch.apply(self, args); // ORIGINAL request, untouched
            });
        })();
      };
    }

    // --- Push 4 Task 1: the finale total IS configurator's own total -------
    // The single [data-cfg-price-total] node (product-info.liquid price
    // block) is the one surface configurator keeps truthful from its private
    // designs[]/state: updatePriceForVariantAndTier (1 case — tier + wallet
    // picks are STATE-gated) and updateCombinedPriceDisplay (2+ cases —
    // summed over designs[]). Both writers re-query document-wide on every
    // write (configurator.js:1043 / :3552, verified), so the node keeps
    // receiving truth wherever it lives. While the finale is open we move
    // its price ROW (total + compare + savings travel together) into the
    // finale's Total slot, and move it home on exit — the ATC relocation
    // pattern, but reversible so the top-of-page price line is whole
    // whenever the finale is closed. The .money spans inside are never
    // touched: Vitals keeps converting them in either location.
    var priceRowHome = null; // { parent, next } — captured on first move
    function findPriceRow() {
      var scope = root.closest('.product-info') ?? document;
      var totalEl = scope.querySelector('[data-cfg-price-total]');
      return totalEl ? totalEl.parentElement : null; // the flex row wrapping total/compare/savings
    }
    function placePriceRowInFinale() {
      var slot = finale ? finale.querySelector('[data-cfg-finale-total]') : null;
      var totalRow = finale ? finale.querySelector('[data-cfg-finale-total-row]') : null;
      var row = findPriceRow();
      if (!row || !slot) return;
      if (!priceRowHome) priceRowHome = { parent: row.parentElement, next: row.nextSibling };
      // The moved spans carry inline em sizes (1.5em / 1.25em). 13px on the
      // slot lands the total at ~19.5px — matching .cfg-finale__total's 19px
      // row — without ever touching the moved node's own styles.
      slot.style.fontSize = '13px';
      slot.appendChild(row);
      if (totalRow) totalRow.hidden = false;
    }
    function restorePriceRow() {
      var slot = finale ? finale.querySelector('[data-cfg-finale-total]') : null;
      var totalRow = finale ? finale.querySelector('[data-cfg-finale-total-row]') : null;
      var row = slot ? slot.firstElementChild : null;
      if (row && priceRowHome && priceRowHome.parent) {
        priceRowHome.parent.insertBefore(row, priceRowHome.next);
      }
      if (totalRow) totalRow.hidden = true;
    }

    // --- Phase 7 Item C: wallet deal badge + thumbnail swatch fallback -----
    // The configurator's green "Save X%" pill (#cfg-wallet-discount-…) is
    // CSS-hidden but still cached and REWRITTEN by configurator.js on every
    // color change and case switch — observing it inherits both refresh
    // triggers for free. pct comes from the read-only CFG_DATA walletMap
    // (Liquid-computed integer — no client math, no currency strings, no
    // price text). The same refresh keeps the thumbnail's no-src fallback a
    // true color swatch via CFG_DATA.colorHexMap.
    var walletImgEl  = document.getElementById('cfg-wallet-img-' + BLOCK_ID);
    var walletDealEl = null;
    function refreshWalletDeal() {
      var data = window['CFG_DATA_' + BLOCK_ID];
      var color = colorNameEl ? colorNameEl.textContent.trim() : '';
      var w = (data && data.walletMap) ? data.walletMap[color] : null;
      var pct = Number((w && w.discountPct) ?? 0);
      if (walletDealEl) {
        if (pct > 0) {
          walletDealEl.textContent = '−' + pct + '% with case';
          walletDealEl.hidden = false;
        } else {
          walletDealEl.hidden = true;
        }
      }
      if (walletImgEl) {
        var hex = (data && data.colorHexMap) ? (data.colorHexMap[color] ?? '') : '';
        // Solid swatch shows only when the img has no usable src (the image,
        // when present, covers the element via object-fit: cover).
        walletImgEl.style.background = hex !== '' ? hex : '';
      }
    }
    (function initWalletDeal() {
      var prices = walletCard ? walletCard.querySelector('.cfg-wallet-prices') : null;
      if (!prices) return;
      walletDealEl = document.createElement('span');
      walletDealEl.className = 'cfg-wallet-deal';
      walletDealEl.hidden = true;
      prices.appendChild(walletDealEl);
      var wDisc = document.getElementById('cfg-wallet-discount-' + BLOCK_ID);
      if (wDisc) {
        new MutationObserver(refreshWalletDeal)
          .observe(wDisc, { childList: true, characterData: true, subtree: true });
      }
      refreshWalletDeal();
    })();

    // --- B.3: styled wallet font dropdown — a PROXY over the native select ----
    // configurator.js holds the select by id and listens 'change' ON THE NODE
    // (configurator.js:2934/3053) — so the styled dropdown (markup in
    // product-info, the case picker's structure with a .cfg-wallet-font-dropdown
    // modifier) sets select.value and dispatches a bubbling change event: the
    // established user-path dispatch pattern, configurator.js untouched. The
    // select stays in the DOM, visually hidden (never display:none).
    // Placeholder semantics match the case picker: "Choose your font" until the
    // USER picks — display-only (configurator's default League Spartan still
    // renders/charges if never touched, exactly as before). Picked-state is
    // per-case; configurator restores the select per case with programmatic
    // writes (no change event), so a case-switch resync re-reads it.
    (function initWalletFontDropdown() {
      var dd = root.querySelector('[data-vs-wfd]');
      var sel = document.getElementById('cfg-wallet-font-' + BLOCK_ID);
      if (!dd || !sel) return;
      var trig = dd.querySelector('.cfg-dropdown__trigger');
      var label = dd.querySelector('.cfg-dropdown__label');
      var menu = dd.querySelector('.cfg-dropdown__menu');
      var items = Array.prototype.slice.call(dd.querySelectorAll('.cfg-dropdown__item'));
      if (!trig || !label || !menu || !items.length) return;
      // B.4: picked-state lives in the SHARED walletFontPicked map (the Finish gate reads it)
      function open() { dd.classList.add('is-open'); trig.setAttribute('aria-expanded', 'true'); }
      function close() { dd.classList.remove('is-open'); trig.setAttribute('aria-expanded', 'false'); }
      function showName(name, family) {
        label.textContent = name;
        label.style.fontFamily = family;
        label.classList.remove('cfg-dropdown__label--placeholder');
      }
      function showPlaceholder() {
        label.textContent = 'Choose your font';
        label.style.fontFamily = '';
        label.classList.add('cfg-dropdown__label--placeholder');
        items.forEach(function (o) { o.setAttribute('aria-selected', 'false'); });
      }
      function choose(it) {
        var family = it.getAttribute('data-font-family');
        var name = it.getAttribute('data-font-name');
        items.forEach(function (o) { o.setAttribute('aria-selected', o === it ? 'true' : 'false'); });
        showName(name, family);
        walletFontPicked[activeCase] = true;
        walletFontWarnClear(); // B.4: picking any font clears the gate warn
        sel.value = family;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      }
      items.forEach(function (it) {
        it.addEventListener('click', function (e) { e.stopPropagation(); choose(it); close(); try { trig.focus(); } catch (err) {} });
      });
      trig.addEventListener('click', function (e) { e.stopPropagation(); if (dd.classList.contains('is-open')) close(); else open(); });
      trig.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
        else if (e.key === 'Escape') { close(); }
      });
      menu.addEventListener('keydown', function (e) { if (e.key === 'Escape') { close(); try { trig.focus(); } catch (err) {} } });
      document.addEventListener('click', function (e) { if (!e.target.closest || !e.target.closest('[data-vs-wfd]')) close(); });
      function resync() {
        if (walletFontPicked[activeCase]) {
          var o = sel.options[sel.selectedIndex];
          if (o) showName(o.text, sel.value);
        } else { showPlaceholder(); }
        walletWarnClearAll(); // B.5: no wallet warn survives a case switch
        close();
      }
      if (casesEl) casesEl.addEventListener('click', function () { setTimeout(function () { syncActiveCase(); resync(); }, 0); });
      if (removeModal) removeModal.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-cfg-modal-confirm]')) {
          // walletFontPicked is reindexed (not wiped) by the main remove
          // handler above — this handler only resyncs the widget display
          // to the newly active (index-shifted) case.
          setTimeout(function () { syncActiveCase(); resync(); }, 250);
        }
      });
    })();

    // Multi-case completion: the active case validates live; other cases via
    // their stored stepper flags. (Stepper-local reads only.)
    function caseComplete(i) {
      if (i === activeCase) return isDone(1) && isDone(2) && isDone(3);
      var f = caseFlags[i];
      return !!(f && f.confirmed[1] && f.confirmed[2] && f.confirmed[3]);
    }
    function firstIncompleteCase() {
      var tabs = casesEl ? casesEl.querySelectorAll('.cfg-case-tab') : [];
      if (!tabs.length) return caseComplete(activeCase) ? -1 : activeCase;
      for (var t = 0; t < tabs.length; t++) {
        var idx = parseInt(tabs[t].getAttribute('data-cfg-case-index'), 10) || 0;
        if (!caseComplete(idx)) return idx;
      }
      return -1;
    }
    function onFinish() {
      var bad = firstIncompleteCase();
      if (bad >= 0) {
        // Jump to the offending case via the configurator's own tab control,
        // open its first incomplete step, and flag it — never proceed, and
        // no loading state on this path (Phase 2.7 behavior, unchanged).
        if (bad !== activeCase && casesEl) {
          var sel = casesEl.querySelector('[data-cfg-select="' + bad + '"]');
          if (sel) sel.click();
        }
        setTimeout(function () {
          syncActiveCase();
          var n = 1;
          for (var k = 1; k <= 3; k++) { if (!isDone(k)) { n = k; break; } }
          goTo(n, true);
          var st = steps[String(n)];
          if (st) st.classList.add('vs-warn');
        }, 0);
        return;
      }
      // B.5: wallet completion gates (G1 text-empty / G2 no-font / G4 no-image;
      // 2026-07-28: both-empty now BLOCKS with both borders — G3 reversed). Per case:
      // active case reads the live DOM; other cases read their snapshots. On a
      // violation Finish jumps to the first offending case, opens step 4, and
      // lights EVERY failing field of that case (re-evaluated on the live DOM
      // after the switch, so warn state never bleeds across cases).
      var wfBad = walletGateFirstViolation();
      if (wfBad >= 0) {
        if (wfBad !== activeCase && casesEl) {
          var wfSel = casesEl.querySelector('[data-cfg-select="' + wfBad + '"]');
          if (wfSel) wfSel.click();
        }
        setTimeout(function () {
          syncActiveCase();
          goTo(4, true);
          walletWarnApply(walletGateViolationsFor(activeCase));
          var wCard = root.querySelector('.cfg-wallet-card') ?? root.querySelector('[data-vs-wfd]');
          if (wCard) settledScroll(wCard);
        }, 0);
        return;
      }
      walletWarnClearAll(); // gates passed — never leave a stale warn behind
      startFinishLoading();
    }
    // B.5 wallet completion gates (hoisted declarations — onFinish runs at
    // click-time). Border-only visual language (the .vs-warn 2px #B3423A
    // treatment) — no message elements. Matrix per case (2026-07-28: G3
    // REVERSED — a SELECTED Text mode must be completed or switched away;
    // both-empty no longer passes as "not personalized", so an active Text
    // wallet can never reach the finale without its fee):
    //   Text mode:  no text -> 'text'   no font -> 'font'   (both empty -> BOTH)
    //   Image mode: no image chosen -> 'image' (G4, unchanged)
    // Evaluation returns EVERY failing field so simultaneous violations all
    // light at once; per-field clear-on-valid is wired below.
    function evalWalletGate(on, mode, hasText, hasImage, fontPicked) {
      var v = [];
      if (on && mode === 'Text') {
        if (!hasText) v.push('text');
        if (!fontPicked) v.push('font');
      } else if (on && mode === 'Image') {
        if (!hasImage) v.push('image');
      }
      return v;
    }
    function walletGateViolationsFor(i) {
      if (i === activeCase) {
        var pill = root.querySelector('.cfg-wallet-pill.is-active');
        var mode = pill ? pill.getAttribute('data-wallet-personalization') : null;
        var t = root.querySelector('.cfg-wallet-textinput');
        var th = root.querySelector('.cfg-wallet-upload-thumb');
        return evalWalletGate(!!(walletToggle && walletToggle.checked), mode,
          !!(t && t.value.trim()), !!(th && !th.hidden), !!walletFontPicked[i]);
      }
      var s = caseSnapshots[i] || {};
      return evalWalletGate(!!s.walletOn, s.walletMode ?? null, !!s.walletHasText, !!s.walletHasImage, !!walletFontPicked[i]);
    }
    function walletGateFirstViolation() {
      var count = Math.max(1, casesEl ? casesEl.querySelectorAll('.cfg-case-tab').length : 1);
      for (var i = 0; i < count; i++) { if (walletGateViolationsFor(i).length) return i; }
      return -1;
    }
    function walletWarnApply(kinds) {
      if (kinds.indexOf('font') >= 0) { var dd = root.querySelector('[data-vs-wfd]'); if (dd) warn(dd.querySelector('.cfg-dropdown__trigger')); }
      if (kinds.indexOf('text') >= 0) warn(root.querySelector('.cfg-wallet-textinput'));
      if (kinds.indexOf('image') >= 0) warn(root.querySelector('.cfg-wallet-upload-btn'));
    }
    function walletWarnClearAll() {
      var dd = root.querySelector('[data-vs-wfd]');
      if (dd) clearWarn(dd.querySelector('.cfg-dropdown__trigger'));
      clearWarn(root.querySelector('.cfg-wallet-textinput'));
      clearWarn(root.querySelector('.cfg-wallet-upload-btn'));
    }
    // B.4-compat: the dropdown widget clears its own control on a pick.
    function walletFontWarnClear() {
      var dd = root.querySelector('[data-vs-wfd]');
      if (dd) clearWarn(dd.querySelector('.cfg-dropdown__trigger'));
    }
    // Clear-on-valid, immediately (not deferred to the next Finish): first
    // non-whitespace character, image chosen, or a mode switch (which clears
    // the OTHER mode's warn state wholesale).
    (function wireWalletWarnClears() {
      var t = root.querySelector('.cfg-wallet-textinput');
      if (t) t.addEventListener('input', function () { if (t.value.trim()) clearWarn(t); });
      var f = root.querySelector('.cfg-wallet-file');
      if (f) f.addEventListener('change', function () { if (f.files && f.files.length) clearWarn(root.querySelector('.cfg-wallet-upload-btn')); });
      root.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('.cfg-wallet-pill')) walletWarnClearAll();
      }, true);
    })();

    // Phase 2.8: Finish loading state — label crossfades to the preparing
    // message with a three-dot pulse (markup injected into the stepper-owned
    // button only), 2.2s (300ms reduced-motion) while the finale mirrors are
    // warmed offscreen so the reveal is instant.
    var finishLabelHTML = null;
    function startFinishLoading() {
      if (!finishBtn || finishBtn.disabled) return;
      captureCaseSnapshot(); // Phase B trigger 3: capture the currently-active case at Finish
      captureCasePreview(); // B.4-3: last-chance configure-time composite + upload
      if (finishLabelHTML == null) finishLabelHTML = finishBtn.innerHTML;
      finishBtn.disabled = true;
      finishBtn.classList.add('is-loading');
      finishBtn.innerHTML = 'Finalizing your Alcantara Case' +
        '<span class="cfg-dots" aria-hidden="true"><i></i><i></i><i></i></span>';
      // Warm the mirrors while the finale is still hidden (one-off draws —
      // the rAF loop only runs while visible).
      updateFinaleContent();
      if (finaleCase) drawContain(sourceCanvas, finaleCase);
      if (finaleWallet && !finaleWallet.hidden) drawContain(walletPreviewCanvas, finaleWallet);
      setTimeout(function () {
        finishBtn.classList.remove('is-loading');
        finishBtn.innerHTML = finishLabelHTML; // stays disabled while finale is open
        enterFinale();
      }, prefersReduced ? 300 : 2200);
    }
    function updateFinaleContent() {
      var wOn = !!(walletToggle && walletToggle.checked);
      var data = window['CFG_DATA_' + BLOCK_ID];
      var color = colorNameEl ? colorNameEl.textContent.trim() : '';
      var w = (data && data.walletMap) ? data.walletMap[color] : null;
      var wModeActive = !!root.querySelector('.cfg-wallet-pill.is-active');
      if (finaleWalletWrap) {
        finaleWalletWrap.hidden = !wOn;
        if (wOn) {
          if (wModeActive && walletPreviewCanvas && walletPreviewCanvas.width > 0) {
            // Personalized wallet: mirror the configurator's wallet canvas.
            if (finaleWallet) finaleWallet.hidden = false;
            if (finaleWalletImg) finaleWalletImg.hidden = true;
          } else {
            // Plain wallet: read-only previewImage URL from the CFG_DATA global.
            if (w && w.previewImage && finaleWalletImg) {
              finaleWalletImg.src = w.previewImage;
              finaleWalletImg.hidden = false;
              if (finaleWallet) finaleWallet.hidden = true;
            } else {
              finaleWalletWrap.hidden = true;
            }
          }
        }
      }
      // --- Phase 8 Push 2: order-summary population. Everything below reads
      // the SAME live state as the wallet branch above (wallet toggle, case
      // personalization mode, wallet personalization mode), so the Task-1
      // case-switch refresh covers the whole card automatically. Prices are
      // NEVER computed or formatted here — wallet lines are walletMap's
      // Liquid-prerendered raw-EUR strings, the total is the matching
      // CFG_DATA.variantPrices string, all written via innerHTML so the
      // Vitals converter keeps rewriting the .money spans.
      var el;
      var mode = activeMode(); // 'Text' | 'Image' | null
      // Captions (textContent — never money).
      el = finale.querySelector('[data-cfg-finale-caption-case]');
      if (el) el.textContent = valueFor(1);
      el = finale.querySelector('[data-cfg-finale-caption-wallet]');
      if (el) el.textContent = color; // rebrand 2026-07-28: color name only (was color + ' suede')
      finale.querySelectorAll('.cfg-finale__caption').forEach(function (c) { c.hidden = false; });
      // Phase 8 Push 3 (Task 6): the spec-line writes are DELETED with its
      // markup — captions carry model + color; personalization shows only as
      // its price row.
      // Fee rows — the active mode's pre-rendered line; both hidden on None.
      el = finale.querySelector('[data-cfg-finale-fee="Text"]');
      if (el) el.hidden = mode !== 'Text';
      el = finale.querySelector('[data-cfg-finale-fee="Image"]');
      if (el) el.hidden = mode !== 'Image';
      // Wallet line — keyed on the toggle + a resolvable walletMap entry
      // (matches what the cart will actually charge, even when the preview
      // image is missing and the visual wrap above stays hidden).
      var walletPriced = wOn && !!w;
      var wRow = finale.querySelector('[data-cfg-finale-line="wallet"]');
      if (wRow) {
        wRow.hidden = !walletPriced;
        if (walletPriced) {
          el = finale.querySelector('[data-cfg-finale-wallet-price]');
          if (el) el.innerHTML = w.priceFormatted ?? '';
          el = finale.querySelector('[data-cfg-finale-wallet-compare]');
          if (el) el.innerHTML = w.compareAtFormatted ?? '';
          var deal = finale.querySelector('[data-cfg-finale-wallet-deal]');
          if (deal) {
            var pct = Number(w.discountPct ?? 0);
            if (pct > 0) {
              deal.textContent = '−' + pct + '% with case';
              deal.hidden = false;
            } else {
              deal.hidden = true; // no compare-at → no badge
            }
          }
        }
      }
      // Push 10 Task 3b: wallet personalization fee rows — VISIBILITY ONLY.
      // The signal is the SAME stepper-side read the step-4 card and the
      // wallet branch above already use (.cfg-wallet-pill.is-active →
      // data-wallet-personalization, configurator-owned class state), gated
      // on walletPriced so a fee can never show without its wallet line.
      // The amounts are Liquid-prerendered raw-EUR .money spans — nothing
      // here writes price text. Push-4 lesson (the €74/€82.99 bug): a
      // displayed price computed in JS from signals that can lag the charged
      // state WILL diverge; toggling a pre-rendered line from the charging
      // signal is the only safe shape.
      var wModePill = root.querySelector('.cfg-wallet-pill.is-active');
      var wMode = wModePill ? wModePill.getAttribute('data-wallet-personalization') : null;
      el = finale.querySelector('[data-cfg-finale-wallet-fee="Text"]');
      if (el) el.hidden = !(walletPriced && wMode === 'Text');
      el = finale.querySelector('[data-cfg-finale-wallet-fee="Image"]');
      if (el) el.hidden = !(walletPriced && wMode === 'Image');
      // Push 4 tombstone: the DOM-signal total computation that lived here
      // (CFG_DATA.variantPrices tier pick keyed on pill class + toggle DOM,
      // active variant only) is DELETED — it priced one case from signals
      // that can lag configurator's internal state, which is how the finale
      // said €74 while the cart POST charged €82.99. The Total row now hosts
      // the RELOCATED [data-cfg-price-total] row (placePriceRowInFinale),
      // which configurator itself keeps truthful from designs[] state.
      // Nothing here may ever write a second number into the Total row.
      el = finale.querySelector('[data-cfg-finale-pricing]');
      if (el) el.hidden = false;
    }

    // ===================================================================
    // Phase D: multi-case order rows (IDENTITY-ONLY this push — no per-case
    // money). Clones [data-cfg-finale-row-tpl] once per case from the Phase-B
    // caseSnapshots into [data-cfg-finale-rows], hides the legacy single-case
    // stage + the itemized per-case price lines, and swaps the heading/subtitle.
    // The relocated order TOTAL row stays the single source of truth for money.
    // Per-case subtotals are DEFERRED (owner-approved: no raw-cents source for
    // the divergence safeguard), so [data-prices-hidden] stays set and the row
    // .money slots stay hidden by the Phase-C CSS. Read-only: reads caseSnapshots
    // + CFG_DATA.walletMap only; never touches configurator state.
    // ===================================================================
    function restoreLegacyStage() {
      // Phase D.1: the SINGLE-CASE hero path AND the no-snapshot safety
      // fallback. Returns the finale to its pre-Phase-D single-case look —
      // legacy stage (case/wallet art + lightbox), itemized price lines,
      // "Your Suede Case" heading, no rows, no subtitle. Idempotent: clears any
      // prior rows + the row-mode flags so a rows->hero switch tears down clean.
      var rowsC = finale.querySelector('[data-cfg-finale-rows]');
      if (rowsC) rowsC.innerHTML = '';
      finale.removeAttribute('data-prices-hidden');
      var totalRowH = finale.querySelector('[data-cfg-finale-total-row]');
      if (totalRowH) {
        totalRowH.style.borderTop = ''; totalRowH.style.marginTop = ''; totalRowH.style.paddingTop = '';
        /* Klaus: the `classList.remove('vs-fin-totals')` that stood here is gone.
           It was the second half of the single-case block — buildFinaleRows now
           applies the totals treatment BEFORE its `caseCount < 2` return, and
           this line ran immediately after and stripped it straight back off.
           Everything else in this function is untouched, so the hero stage keeps
           its stage art, heading, case line, meta/count reset and dense flag. */
      }
      var metaH = finale.querySelector('[data-cfg-finale-meta]');
      if (metaH) metaH.hidden = true;                // meta line is rows-mode only
      var countH = finale.querySelector('[data-cfg-finale-count]');
      if (countH) countH.textContent = '';
      root.removeAttribute('data-vs-dense');         // B.1: density is rows-mode state
      var stage = finale.querySelector('.cfg-finale__stage');
      if (stage) stage.style.display = '';
      var caseLine = finale.querySelector('[data-cfg-finale-line="case"]');
      if (caseLine) caseLine.hidden = false; // always-on line updateFinaleContent doesn't manage
      var heading = finale.querySelector('[data-cfg-finale-heading]');
      if (heading) heading.textContent = 'Your Alcantara Case';
      var subtitle = finale.querySelector('[data-cfg-finale-subtitle]');
      if (subtitle) { subtitle.hidden = true; subtitle.textContent = ''; }
    }
    // ---- Row-detail pricing ----------------------------------------------
    // Personalization fee cents come from the upgrade product (Text/Image/Design
    // — all a flat fee today), fetched once + cached. Case cents from
    // CFG_DATA.variants[]; wallet cents from walletMap. Every figure is emitted
    // through fmtMoney() (a byte-exact clone of configurator's formatMoneyCents)
    // into .money spans so Vitals converts them AND a summed subtotal string can
    // be compared 1:1 with configurator's [data-cfg-price-total] (the safeguard).
    var UPGRADE_CENTS = null;      // { <variantTitle>: cents }
    var upgradeFetchStarted = false;
    var pricesWarned = false;
    function ensureUpgradeCents() {
      if (UPGRADE_CENTS || upgradeFetchStarted) return;
      upgradeFetchStarted = true;
      var data = window['CFG_DATA_' + BLOCK_ID];
      var handle = data && data.upgradeProductHandle;
      if (!handle) return;
      fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.variants) return;
          var m = {};
          p.variants.forEach(function (v) { m[v.title] = Number(v.price) || 0; });
          UPGRADE_CENTS = m;
          if (finale && !finale.hidden) buildFinaleRows(); // fees known → repopulate + re-check
        })
        .catch(function () { /* fees stay null → safeguard hides prices (safe) */ });
    }
    function upgradeFee(mode) {
      if (!mode || !UPGRADE_CENTS) return 0; // null fees → 0 → safeguard catches the shortfall
      return Number(UPGRADE_CENTS[mode] ?? UPGRADE_CENTS.Text ?? UPGRADE_CENTS.Image ?? 0) || 0;
    }
    function fmtMoney(cents) {
      var fmt = (window.themeVariables && themeVariables.settings && themeVariables.settings.moneyFormat)
                || (window.Shopify && Shopify.money_format) || '€{{amount}}';
      function fmtNumber(num, precision, thousands, decimal) {
        var fixed = (num / 100).toFixed(precision);
        var parts = fixed.split('.');
        var intPart = parts[0].replace(/(\d)(?=(\d{3})+$)/g, '$1' + (thousands ?? ','));
        var decPart = (parts.length > 1) ? (decimal ?? '.') + parts[1] : '';
        return intPart + decPart;
      }
      var amount = fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (mm, token) {
        switch (token) {
          case 'amount': return fmtNumber(cents, 2, ',', '.');
          case 'amount_no_decimals': return fmtNumber(cents, 0, ',', '.');
          case 'amount_with_comma_separator': return fmtNumber(cents, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fmtNumber(cents, 0, '.', ',');
          case 'amount_with_apostrophe_separator': return fmtNumber(cents, 2, '’', '.');
          default: return fmtNumber(cents, 2, ',', '.');
        }
      });
      return /class\s*=\s*["']?[^"'>]*\bmoney\b/.test(amount) ? amount : '<span class="money">' + amount + '</span>';
    }
    function moneyText(html) { var d = document.createElement('div'); d.innerHTML = html; return (d.textContent || '').replace(/\s+/g, ' ').trim(); }
    // Write current (+ optional strikethrough compare) into a container as real
    // .money spans (innerHTML, never textContent). The container is never itself
    // .money — avoids nesting Vitals would double-process.
    function setMoney(el, currentCents, compareCents) {
      if (!el) return;
      el.classList.remove('money');
      var html = '';
      if (compareCents != null && compareCents > currentCents) {
        html += '<s style="color:#9a9a9a;font-weight:400;margin-right:6px;">' + fmtMoney(compareCents) + '</s>';
      }
      html += fmtMoney(currentCents);
      el.innerHTML = html;
    }
    // Push B: write a compare/current PAIR into two dedicated .money spans (the
    // markup owns the stacking + strike styling via tokens, so no inline colour
    // literals here). fmtMoney() already emits a .money wrapper, so the outer span
    // drops its own .money class to avoid nesting Vitals would double-process.
    function setPair(wasEl, nowEl, currentCents, compareCents) {
      if (wasEl) {
        if (compareCents != null && compareCents > currentCents) {
          wasEl.classList.remove('money');
          wasEl.innerHTML = fmtMoney(compareCents);
          wasEl.hidden = false;
        } else { wasEl.innerHTML = ''; wasEl.hidden = true; }
      }
      if (nowEl) { nowEl.classList.remove('money'); nowEl.innerHTML = fmtMoney(currentCents); }
    }
    function caseVariantCents(model, colorName) {
      var data = window['CFG_DATA_' + BLOCK_ID];
      var vs = (data && data.variants) ? data.variants : [];
      var found = null;
      for (var vi = 0; vi < vs.length; vi++) {
        var o = vs[vi].options || [];
        if (o[0] === model && o[1] === colorName) { found = vs[vi]; break; }
      }
      if (!found) found = vs[0] || null; // uniform-pricing fallback
      if (!found) return { price: 0, compare: 0 };
      var price = Number(found.price) || 0;
      var cmp = Number(found.compare_at_price) || 0;
      return { price: price, compare: (cmp > price ? cmp : price) };
    }
    // Per-case price parts (cents) — mirrors configurator.casesPriceTotals so the
    // summed current subtotal equals [data-cfg-price-total]. The flat fee is
    // added to BOTH current and compare (no discount on the fee), as configurator does.
    function caseParts(s) {
      var data = window['CFG_DATA_' + BLOCK_ID];
      var wmap = (data && data.walletMap) ? data.walletMap : {};
      var cv = caseVariantCents(s.model, s.colorName);
      var caseFee = upgradeFee(s.mode);
      var wEntry = (s.walletOn && s.colorName && wmap[s.colorName] && wmap[s.colorName].available) ? wmap[s.colorName] : null;
      var wPrice = wEntry ? (Number(wEntry.priceCents) || 0) : 0;
      var wCmpRaw = wEntry ? (Number(wEntry.compareAtCents) || 0) : 0;
      var wCmp = (wCmpRaw > wPrice) ? wCmpRaw : wPrice;
      var wFee = (wEntry && s.walletMode) ? upgradeFee(s.walletMode) : 0;
      return {
        casePrice: cv.price, caseCompare: cv.compare, caseFee: caseFee,
        walletOn: !!wEntry, walletPrice: wPrice, walletCompare: wCmp,
        walletDiscount: wEntry ? (Number(wEntry.discountPct) || 0) : 0, walletFee: wFee,
        persTotal: caseFee + wFee,
        subtotal: cv.price + caseFee + wPrice + wFee,
        subtotalCompare: cv.compare + caseFee + wCmp + wFee
      };
    }

    function buildFinaleRows() {
      if (!finale) return;
      var rowsC = finale.querySelector('[data-cfg-finale-rows]');
      var tpl = finale.querySelector('[data-cfg-finale-row-tpl]');
      if (!rowsC || !tpl || !tpl.content) return;
      var data = window['CFG_DATA_' + BLOCK_ID];
      var wmap = (data && data.walletMap) ? data.walletMap : {};
      var caseCount = Math.max(1, casesEl ? casesEl.querySelectorAll('.cfg-case-tab').length : 1);
      // Totals: the vs-fin treatment — label + savings on the left, compare-at
      // and the grand total on the right. The savings node is configurator-owned
      // but both of its writers re-query document-wide, so moving it is safe.
      // MOVED here from below the row-building loop (Klaus): it used to sit
      // after the `caseCount < 2` early return, so a SINGLE-case finale — the
      // common path — never got it and the savings pill stayed wedged in the
      // price box, wrapping onto its own line. Hoisted above the return so both
      // modes get identical totals; the block itself is unchanged. It is placed
      // AFTER the rowsC/tpl guard above deliberately — those nodes are present
      // on every finale, so nothing is skipped.
      var totalRow = finale.querySelector('[data-cfg-finale-total-row]');
      if (totalRow) {
        totalRow.classList.add('vs-fin-totals');
        totalRow.style.borderTop = ''; totalRow.style.marginTop = ''; totalRow.style.paddingTop = '';
        /* Klaus (finale defect 3): the badge relocation that stood here —
           leftGrp.appendChild(savings) — is DELETED. Parking the savings pill
           in .vs-fin-totals__left painted it BEFORE the prices, and on phones
           the collapsing left column stacked it under TOTAL with its text
           wrapped. Left in its NATIVE box (the relocated configurator price
           row, product-info.liquid:65-71) the DOM order price→compare→badge
           plus compare's order:-1 renders compare → price → badge — the
           required order, with zero JS. The vs-fin-totals class add and the
           inline clears above are kept. */
      }
      // Phase D.1: a SINGLE case keeps the legacy hero stage. Rows are for 2+.
      if (caseCount < 2) { restoreLegacyStage(); return; }
      var haveAny = false;
      for (var k = 0; k < caseCount; k++) { if (caseSnapshots[k]) { haveAny = true; break; } }
      if (!haveAny) { restoreLegacyStage(); return; }
      ensureUpgradeCents();

      rowsC.innerHTML = ''; // rebuild fresh (idempotent on re-entry / case switch)
      var sumCurrent = 0;
      for (var i = 0; i < caseCount; i++) {
        var s = caseSnapshots[i] || {};
        var p = caseParts(s);
        sumCurrent += p.subtotal;
        var node = tpl.content.firstElementChild.cloneNode(true);

        // Thumbs — all geometry lives in CSS tokens now (no inline literals). The
        // case snapshot is already cropped to the art and drops into a fixed
        // 1:2.00 box with object-fit:contain, so every row is the same height.
        var thumb = node.querySelector('[data-cfg-finale-row-thumb]');
        if (thumb && s.thumbDataURL) {
          var img = document.createElement('img');
          img.src = s.thumbDataURL;
          img.alt = 'Case ' + (i + 1) + ' preview';
          thumb.appendChild(img);
        }
        // Wallet: previewImage (800px frame, 435px of content) CSS-cropped by the
        // fixed percentages in vs-finale-css. The node is UNCONDITIONAL — when the
        // case has no wallet the row gets .vs-fin-row--nowallet, which only sets
        // visibility:hidden, so --thumb-col keeps its width and the spine holds.
        var wThumb = node.querySelector('[data-cfg-finale-row-wallet-thumb]');
        var wEntryT = (s.walletOn && s.colorName && wmap[s.colorName]) ? wmap[s.colorName] : null;
        // B.3: an engraved wallet uses its per-case SNAPSHOT (already cropped to
        // the silhouette -> contain-fit via the --wallet-snap class); a clean
        // wallet keeps the CDN previewImage + CSS percentage-crop, untouched.
        var wSnap = (s.walletOn && s.walletThumbDataURL) ? s.walletThumbDataURL : null;
        var wArt = wSnap || (wEntryT ? (wEntryT.previewImage || wEntryT.image) : null);
        if (wThumb && wArt) {
          if (wSnap) wThumb.classList.add('vs-fin-thumb--wallet-snap');
          var wImg = document.createElement('img');
          wImg.src = wArt;
          wImg.alt = 'Case ' + (i + 1) + ' matching wallet';
          wThumb.appendChild(wImg);
        } else {
          node.classList.add('vs-fin-row--nowallet');
        }

        // text: "Case N" / colour · personalization
        var caseEl = node.querySelector('[data-cfg-finale-row-case]');
        if (caseEl) caseEl.textContent = 'Case ' + (i + 1);
        var descEl = node.querySelector('[data-cfg-finale-row-desc]');
        var descText = '';
        if (descEl) {
          var dparts = [];
          if (s.colorName) dparts.push(s.colorName);
          if (s.summary) dparts.push(s.summary);
          descText = dparts.join(' · ');
          descEl.textContent = descText;
        }

        // per-case subtotal — compare-at stacked ABOVE the real price, right-aligned
        setPair(node.querySelector('[data-cfg-finale-row-sum-was]'),
                node.querySelector('[data-cfg-finale-row-sum-money]'), p.subtotal, p.subtotalCompare);

        // expansion: itemized lines (case / case fee / wallet / wallet fee)
        var bdCase = node.querySelector('[data-cfg-finale-row-bd-case]');
        if (bdCase) setPair(bdCase.querySelector('[data-cfg-finale-row-bd-case-was]'),
                            bdCase.querySelector('[data-cfg-finale-row-bd-case-money]'), p.casePrice, p.caseCompare);
        // B.2: the combined personalization line is SPLIT per item — each line
        // shows only when that fee exists. Same cents sources (caseParts), same
        // .money innerHTML path; caseFee + walletFee === the old persTotal, so
        // the subtotal and order total are untouched (presentation split only).
        var bdPersCase = node.querySelector('[data-cfg-finale-row-bd-pers-case]');
        if (bdPersCase) {
          if (p.caseFee > 0) {
            setPair(null, bdPersCase.querySelector('[data-cfg-finale-row-bd-pers-case-money]'), p.caseFee, null);
            bdPersCase.hidden = false;
          } else bdPersCase.hidden = true;
        }
        var bdPersWallet = node.querySelector('[data-cfg-finale-row-bd-pers-wallet]');
        if (bdPersWallet) {
          if (p.walletFee > 0) {
            setPair(null, bdPersWallet.querySelector('[data-cfg-finale-row-bd-pers-wallet-money]'), p.walletFee, null);
            bdPersWallet.hidden = false;
          } else bdPersWallet.hidden = true;
        }
        var bdWallet = node.querySelector('[data-cfg-finale-row-bd-wallet]');
        if (bdWallet) {
          if (p.walletOn) {
            setPair(bdWallet.querySelector('[data-cfg-finale-row-bd-wallet-was]'),
                    bdWallet.querySelector('[data-cfg-finale-row-bd-wallet-money]'), p.walletPrice, p.walletCompare);
            var pct = bdWallet.querySelector('[data-cfg-finale-row-bd-wallet-pct]');
            if (pct) {
              if (p.walletDiscount > 0) { pct.textContent = '−' + p.walletDiscount + '%'; pct.hidden = false; }
              else pct.hidden = true;
            }
            bdWallet.hidden = false;
          } else bdWallet.hidden = true;
        }

        // interactions: head tap = expand/collapse (8px slop); "Edit this case"
        // inside the expansion = edit-jump. Rows open INDEPENDENTLY (Step 6).
        var head = node.querySelector('[data-cfg-finale-row-toggle]');
        var body = node.querySelector('[data-cfg-finale-row-body]');
        if (head) {
          head.setAttribute('aria-expanded', 'false');
          head.setAttribute('aria-label', 'Case ' + (i + 1) + (descText ? ' — ' + descText : '') + ' — view details');
          if (body) {
            var bid = 'vs-fin-body-' + BLOCK_ID + '-' + i;
            body.id = bid;
            head.setAttribute('aria-controls', bid);
          }
          bindRowToggle(head, node);
        }
        var editBtn = node.querySelector('[data-cfg-finale-row-edit]');
        if (editBtn) { (function (idx) { editBtn.addEventListener('click', function (e) { e.stopPropagation(); editCase(idx); }); })(i); }
        rowsC.appendChild(node);
      }

      // Adaptive density (Step 4): 4 or 5 cases scale --case-w to 78% of base.
      // B.1: the dense flag lives on the configurator ROOT (same element as the
      // CSS tokens) — a descendant-level custom-property override cannot
      // re-evaluate the derived --thumb-col/--spine chains (the Push-B attr on
      // rowsC silently kept the 56px thumb column in dense mode). Writing a
      // data-* on root follows the existing root.dataset.vsStepper precedent.
      if (caseCount >= 4) root.setAttribute('data-vs-dense', '1');
      else root.removeAttribute('data-vs-dense');
      // SAFEGUARD: sum current subtotals, format via the SAME helper, and
      // compare against configurator's [data-cfg-price-total] text (captured
      // here — pre-Vitals for base-currency shoppers). Mismatch → hide all row
      // prices (Phase C CSS) + warn once; the order total stays authoritative.
      var scope = root.closest('.product-info') ?? document;
      var totalEl = scope.querySelector('[data-cfg-price-total]');
      var configTotal = '';
      if (totalEl) {
        // Resolve to the BASE-currency (EUR) string regardless of Vitals timing:
        // after conversion Vitals preserves the original on the .money span as
        // data-vitals-cc-eur; before conversion the textContent is still EUR.
        // Either way we compare EUR-to-EUR against fmtMoney(), so a currency
        // switch never falsely trips the safeguard.
        var mEl = totalEl.querySelector('.money');
        var eurOrig = (mEl && mEl.getAttribute) ? mEl.getAttribute('data-vitals-cc-eur') : null;
        configTotal = (eurOrig || totalEl.textContent || '').replace(/\s+/g, ' ').trim();
      }
      var mySum = moneyText(fmtMoney(sumCurrent));
      if (configTotal !== '' && configTotal === mySum) {
        finale.removeAttribute('data-prices-hidden'); // show per-case prices
      } else {
        finale.setAttribute('data-prices-hidden', ''); // identity-only fallback
        if (!pricesWarned) { pricesWarned = true; try { console.warn('[vs-stepper] per-case subtotals (' + mySum + ') do not match the order total (' + configTotal + ') — hiding per-case prices; order total remains authoritative.'); } catch (e) {} }
      }

      // Activate the row layout: hide the legacy stage + the OLD itemized
      // per-case price lines (KEEP the order TOTAL row); swap heading/subtitle.
      var stage2 = finale.querySelector('.cfg-finale__stage');
      if (stage2) stage2.style.display = 'none';
      ['[data-cfg-finale-line="case"]',
       '[data-cfg-finale-fee="Text"]', '[data-cfg-finale-fee="Image"]',
       '[data-cfg-finale-line="wallet"]',
       '[data-cfg-finale-wallet-fee="Text"]', '[data-cfg-finale-wallet-fee="Image"]'
      ].forEach(function (sel) { var e = finale.querySelector(sel); if (e) e.hidden = true; });
      var heading = finale.querySelector('[data-cfg-finale-heading]');
      if (heading) heading.textContent = 'Your order';
      // The count now lives in the meta line; the legacy subtitle stays empty+hidden.
      var subtitle = finale.querySelector('[data-cfg-finale-subtitle]');
      if (subtitle) { subtitle.textContent = ''; subtitle.hidden = true; }
      var meta = finale.querySelector('[data-cfg-finale-meta]');
      var countEl = finale.querySelector('[data-cfg-finale-count]');
      if (countEl) countEl.textContent = caseCount + (caseCount === 1 ? ' case' : ' cases');
      if (meta) meta.hidden = false;
      bindExpandAll();
      refreshExpandAll();
    }
    // Step 6: Expand all / Collapse all. Label reflects the CURRENT state; bound
    // once (idempotent) since buildFinaleRows re-runs on every case switch.
    var expandAllBound = false;
    function rowsList() {
      var rc = finale ? finale.querySelector('[data-cfg-finale-rows]') : null;
      return rc ? Array.prototype.slice.call(rc.querySelectorAll('[data-cfg-finale-row]')) : [];
    }
    function refreshExpandAll() {
      var btn = finale ? finale.querySelector('[data-cfg-finale-expand-all]') : null;
      if (!btn) return;
      var rows = rowsList();
      var openCount = rows.filter(function (r) { return r.classList.contains('is-open'); }).length;
      var allOpen = rows.length > 0 && openCount === rows.length;
      btn.textContent = allOpen ? 'Collapse all' : 'Expand all';
      btn.setAttribute('aria-expanded', allOpen ? 'true' : 'false');
    }
    function bindExpandAll() {
      if (expandAllBound || !finale) return;
      var btn = finale.querySelector('[data-cfg-finale-expand-all]');
      if (!btn) return;
      expandAllBound = true;
      btn.addEventListener('click', function () {
        var rows = rowsList();
        var allOpen = rows.length > 0 && rows.every(function (r) { return r.classList.contains('is-open'); });
        rows.forEach(function (r) { setRowOpen(r, !allOpen); });
        refreshExpandAll();
      });
    }
    function setRowOpen(rowEl, open) {
      rowEl.classList.toggle('is-open', open);
      var h = rowEl.querySelector('[data-cfg-finale-row-toggle]');
      if (h) h.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    // Head tap/keyboard = expand/collapse (accordion: one open at a time). 8px
    // slop so a scroll never toggles; keyboard (detail===0) always toggles.
    function bindRowToggle(head, rowEl) {
      var sx = 0, sy = 0, moved = false;
      head.addEventListener('pointerdown', function (e) { sx = e.clientX; sy = e.clientY; moved = false; }, { passive: true });
      head.addEventListener('pointermove', function (e) { if (Math.abs(e.clientX - sx) > 8 || Math.abs(e.clientY - sy) > 8) moved = true; }, { passive: true });
      head.addEventListener('click', function (e) { if (e.detail !== 0 && moved) return; toggleRow(rowEl); });
    }
    // Step 6: rows expand INDEPENDENTLY. The accordion loop that stripped .is-open
    // from every other row is deleted — nothing depended on single-open (the WAAPI
    // stagger is index-based, the rAF mirrors only drive the hero stage canvases,
    // and the one scroll is the single settledScroll at reveal).
    function toggleRow(rowEl) {
      setRowOpen(rowEl, !rowEl.classList.contains('is-open'));
      refreshExpandAll();
    }
    // Edit-jump — from the "Edit this case" control inside the expansion; also
    // the destination onFinish's incomplete-case jump uses.
    function editCase(idx) {
      if (idx !== activeCase && casesEl) {
        var sel = casesEl.querySelector('[data-cfg-select="' + idx + '"]');
        if (sel) sel.click(); // configurator switches + loads that design
      }
      setTimeout(function () { syncActiveCase(); goTo(1, true); }, 0); // goTo hides the finale + opens step 1
    }
    ensureUpgradeCents(); // warm the fee cache at init so prices are ready by finale time
    function drawContain(src, dst) {
      if (!src || !dst || !src.width || !src.height) return;
      var ctx = dst.getContext('2d');
      if (!ctx) return;
      var s = Math.min(dst.width / src.width, dst.height / src.height);
      var dW = src.width * s, dH = src.height * s;
      ctx.clearRect(0, 0, dst.width, dst.height);
      try { ctx.drawImage(src, (dst.width - dW) / 2, (dst.height - dH) / 2, dW, dH); }
      catch (e) { /* source mid-resize — next frame */ }
    }
    function finaleTick() {
      finaleRafId = null;
      if (!finale || finale.hidden || document.hidden) return; // stopped/paused
      if (finaleCase) drawContain(sourceCanvas, finaleCase);
      if (finaleWallet && !finaleWallet.hidden) drawContain(walletPreviewCanvas, finaleWallet);
      finaleRafId = requestAnimationFrame(finaleTick);
    }
    function startFinaleMirror() {
      if (finaleRafId == null && finale && !finale.hidden && !document.hidden) {
        finaleRafId = requestAnimationFrame(finaleTick);
      }
    }
    document.addEventListener('visibilitychange', startFinaleMirror);
    // Phase 2.8: FINALE-IN-PLACE — the section stays where Liquid put it,
    // directly after Step 4's card. No repositioning, no scroll-to-top, no
    // gallery hiding. Reveal is a staged sequence driven by the
    // .is-revealing CSS (panel rise → case → wallet +80ms → summary +80ms →
    // ATC last), then ONE settledScroll to the finale heading.
    var finaleHeading = finale ? finale.querySelector('.cfg-finale__heading') : null;
    // Phase 1.5 Fix 4b: scroll-based hide — a step scrolled ≥30% into view
    // while the finale is open hides it. Armed ONLY after the post-Finish
    // scroll settles (settledScroll onDone + 100ms), disarmed whenever the
    // finale hides — without that guard the Finish scroll itself would
    // trigger the hide. Steps 1–3 only: Step 4 sits directly ABOVE the
    // adjacent finale, so on tall viewports it can be legitimately visible
    // while the finale is open and would false-trigger (deviation, reported).
    // Phase 4 Task 4b: scroll-based finale hiding is DELETED — scrolling
    // never hides the finale in any direction. Hiding is interaction-only:
    // goTo paths, and a completed CLICK inside any step square (wired below).
    function enterFinale() {
      if (!finale) return;
      // Phase 6D: the inspect-lightbox stage listener binds HERE, on the
      // finale's first reveal — zero listeners, markup, or permission calls
      // exist before this moment (D5). lbStageClick is hoisted from the 6D
      // block below; lbBound starts undefined (falsy).
      if (!lbBound) { lbBound = true; finale.addEventListener('click', lbStageClick); }
      updateFinaleContent();
      placePriceRowInFinale(); // Push 4 Task 1 — configurator's total moves in
      buildFinaleRows(); // Phase D — build the per-case order rows (hides the legacy stage)
      finale.hidden = false;
      renderAll(); // Fix 5b: step 4 → done-row while the finale is open
      if (!prefersReduced) {
        finale.classList.add('is-revealing');
        setTimeout(function () { finale.classList.remove('is-revealing'); }, 900);
        // Phase F: stagger the rows in on reveal (ROWS path only; the hero uses
        // the existing two-item stage choreography, untouched — revealRows is
        // empty for a single-case hero). WAAPI with fill:'backwards' shows the
        // from-state during each row's delay, then settles to the element's
        // natural style — no stuck-at-0 if a frame is throttled. 50ms/row →
        // 5 rows settle by ~460ms (< 500ms). Reduced-motion skips this whole
        // block, so rows just appear.
        var revealRows = finale.querySelectorAll('[data-cfg-finale-rows] [data-cfg-finale-row]');
        Array.prototype.forEach.call(revealRows, function (r, i) {
          if (typeof r.animate !== 'function') return;
          r.animate(
            [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'none' }],
            { duration: 260, delay: i * 50, easing: 'cubic-bezier(.4, 0, .2, 1)', fill: 'backwards' }
          );
        });
      }
      startFinaleMirror();
      // Phase 4 Task 4a: land the finale's TOP under the sticky area (its
      // top edge wins if the finale is taller than the viewport).
      settledScroll(finale);
    }
    function exitFinale(returnToStep4) {
      if (!finale || finale.hidden) return;
      function finish() {
        finale.hidden = true; // finaleTick self-stops on hidden
        restorePriceRow(); // Push 4 Task 1 — price row back to the top price line
        finale.classList.remove('is-exiting');
        if (finishBtn) finishBtn.disabled = false;
        if (returnToStep4) goTo(4, true);
        else renderAll(); // Fix 5b: re-expand step 4 to its prior state
      }
      if (prefersReduced) { finish(); return; }
      finale.classList.add('is-exiting'); // 180ms reverse fade
      setTimeout(finish, 190);
    }
    // Phase 3 Task 5d: "Start Over" asks first. Reuses the .cfg-modal family
    // (square, white, existing z-tier). NO / backdrop / Escape = close only;
    // YES = close + leave the finale + reopen Step 1 with ALL selections
    // preserved (goTo's finale-guard hides the finale; no state is wiped).
    var startOverModal = root.querySelector('[data-cfg-startover]');
    function openStartOver() {
      if (!startOverModal) { goTo(1, true); return; }
      startOverModal.hidden = false;
      if (prefersReduced) {
        startOverModal.classList.add('is-open');
      } else {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { startOverModal.classList.add('is-open'); });
        });
      }
      var noBtn = startOverModal.querySelector('.cfg-modal__btn--cancel');
      if (noBtn) setTimeout(function () { try { noBtn.focus(); } catch (e) {} }, prefersReduced ? 0 : 90);
    }
    function closeStartOver() {
      if (!startOverModal) return;
      startOverModal.classList.remove('is-open');
      if (prefersReduced) {
        startOverModal.hidden = true;
      } else {
        setTimeout(function () {
          if (!startOverModal.classList.contains('is-open')) startOverModal.hidden = true;
        }, 210);
      }
    }
    if (startOverModal) {
      startOverModal.addEventListener('click', function (e) {
        if (e.target.closest('[data-cfg-startover-yes]')) {
          // Phase 8 Task 8: YES = FULL reset. The configurator keeps no
          // localStorage/sessionStorage (grep-verified), so a clean reload
          // guarantees every case, upload, wallet substate, and stepper flag
          // is gone and the page boots at a single default Case 1 with
          // step 1 open. scrollRestoration 'manual' stops the browser from
          // restoring the old scroll offset — the fresh page lands at the
          // top with step 1 in view. Trade-off (accepted): full refetch,
          // scroll position deliberately discarded. Cart untouched.
          try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch (err) {}
          window.location.reload();
        } else if (e.target.closest('[data-cfg-startover-no]')) {
          closeStartOver();
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && startOverModal.classList.contains('is-open')) closeStartOver();
      });
    }
    if (finaleEdit) {
      finaleEdit.addEventListener('click', function () { openStartOver(); });
    }
    // --- Phase 6D: finale inspect lightbox (tilt + sheen + zoom) -----------
    // Tapping either finale preview opens a stepper-owned white overlay with
    // the composite at max source resolution: pinch/wheel zoom to LB_MAXZOOM,
    // drag/two-finger pan when zoomed, double-tap/double-click toggles zoom,
    // deviceorientation tilt (±10°) with an opposite-tracking sheen, pointer
    // fallback, idle drift at zoom 1. Sources are the STEPPER-OWNED finale
    // mirrors (finaleCase / finaleWallet / finaleWalletImg) — no
    // configurator-owned element is read or written here. Zero cost before
    // the finale first opens: DOM built on first open, stage listener bound
    // in enterFinale, iOS 13+ DeviceOrientationEvent.requestPermission()
    // called ONLY inside the opening tap handler (user gesture), no
    // pre-prompt UI.
    var LB_MAXZOOM = 2.0; // Phase 6C: 600px canvas source / 300px displayed = 2.0, clamp(1.5, 2.5)
    var lbBound = false;      // stage listener bound (set in enterFinale)
    var lbEl = null;          // overlay root — built on first open
    var lbPersp, lbZoomEl, lbTiltEl, lbCanvas, lbImg, lbSheen;
    var lbZ = 1, lbTX = 0, lbTY = 0;         // zoom / pan
    var lbRX = 0, lbRY = 0;                  // tilt degrees
    var lbGyro = false;                      // usable deviceorientation arrived
    var lbGyroAsked = false;                 // permission requested once
    var lbBeta0 = null, lbGamma0 = null;     // per-open neutral calibration
    var lbLastInput = 0;                     // last tilt-driver activity (drift gate)
    var lbDriftRaf = null;
    var lbMoved = false;                     // pan/pinch happened → suppress tap-close
    var lbPointers = {};                     // active pointers (pinch/pan)
    var lbPinchBase = null;
    var lbLastTap = 0;                       // manual double-tap detection
    var lbPrevOverflow = null;               // scroll-lock restore pair
    function lbClamp(v, lim) { return Math.max(-lim, Math.min(lim, v)); }
    function lbApplyZoom() {
      if (!lbZoomEl) return;
      var media = lbCanvas.hidden ? lbImg : lbCanvas;
      var r = media.getBoundingClientRect();
      var baseW = r.width / lbZ, baseH = r.height / lbZ;
      lbTX = lbClamp(lbTX, (lbZ - 1) * baseW / 2);
      lbTY = lbClamp(lbTY, (lbZ - 1) * baseH / 2);
      lbZoomEl.style.transform = 'translate(' + lbTX + 'px,' + lbTY + 'px) scale(' + lbZ + ')';
      if (lbZ > 1.2) lbSetTilt(0, 0); // tilt pauses while inspecting zoomed
    }
    function lbSetZoom(z) {
      lbZ = Math.max(1, Math.min(LB_MAXZOOM, z));
      if (lbZ - 1 < 0.001) lbZ = 1; // float snap — wheel steps must land back on exactly 1
      if (lbZ === 1) { lbTX = 0; lbTY = 0; }
      lbApplyZoom();
    }
    function lbSetTilt(rx, ry) {
      if (!lbTiltEl || prefersReduced) return;
      if (lbZ > 1.2) { rx = 0; ry = 0; }
      lbRX = lbClamp(rx, 10); lbRY = lbClamp(ry, 10);
      lbTiltEl.style.transform = 'rotateX(' + lbRX + 'deg) rotateY(' + lbRY + 'deg)';
      // Sheen center tracks OPPOSITE the tilt; opacity scales with magnitude.
      var mag = Math.min(1, Math.sqrt(lbRX * lbRX + lbRY * lbRY) / 10);
      lbSheen.style.background = 'radial-gradient(circle at ' +
        (50 - lbRY * 4.5) + '% ' + (50 + lbRX * 4.5) + '%, rgba(255,255,255,.9), rgba(255,255,255,0) 60%)';
      lbSheen.style.opacity = (mag * 0.25).toFixed(3);
    }
    function lbDrift(now) {
      lbDriftRaf = null;
      if (!lbEl || lbEl.hidden || prefersReduced) return;
      if (lbZ === 1 && now - lbLastInput > 2500) {
        var t = now / 4000 * Math.PI * 2; // ±3°, ~4s period
        lbSetTilt(3 * Math.sin(t), 3 * Math.sin(t + Math.PI / 2));
      }
      lbDriftRaf = requestAnimationFrame(lbDrift);
    }
    function lbOrientHandler(e) {
      if (e.beta == null && e.gamma == null) return;
      lbGyro = true;
      lbLastInput = performance.now();
      if (lbBeta0 == null) { lbBeta0 = e.beta ?? 0; lbGamma0 = e.gamma ?? 0; }
      lbSetTilt((e.beta ?? 0) - lbBeta0, (e.gamma ?? 0) - lbGamma0);
    }
    function lbEnsureTiltDriver() {
      // Called INSIDE the opening tap handler — the user gesture iOS 13+
      // requires. Denied/unavailable: the pointer-move fallback (bound in
      // lbBuild, gated by lbGyro) drives the same tilt.
      if (prefersReduced) return;
      window.addEventListener('deviceorientation', lbOrientHandler);
      if (lbGyroAsked) return;
      lbGyroAsked = true;
      var DOE = window.DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === 'function') {
        try { DOE.requestPermission().catch(function () {}); } catch (e) {}
      }
    }
    function lbBuild() {
      lbEl = document.createElement('div');
      lbEl.className = 'cfg-lightbox';
      lbEl.setAttribute('data-cfg-lightbox', '');
      lbEl.hidden = true;
      lbEl.innerHTML =
        '<button type="button" class="cfg-lightbox__close" aria-label="Close">&times;</button>' +
        '<div class="cfg-lightbox__persp">' +
          '<div class="cfg-lightbox__zoom">' +
            '<div class="cfg-lightbox__tilt">' +
              '<canvas class="cfg-lightbox__media" hidden></canvas>' +
              '<img class="cfg-lightbox__media" alt="" hidden>' +
              '<div class="cfg-lightbox__sheen" aria-hidden="true"></div>' +
            '</div>' +
          '</div>' +
        '</div>';
      document.body.appendChild(lbEl);
      lbPersp  = lbEl.querySelector('.cfg-lightbox__persp');
      lbZoomEl = lbEl.querySelector('.cfg-lightbox__zoom');
      lbTiltEl = lbEl.querySelector('.cfg-lightbox__tilt');
      lbCanvas = lbEl.querySelector('canvas.cfg-lightbox__media');
      lbImg    = lbEl.querySelector('img.cfg-lightbox__media');
      lbSheen  = lbEl.querySelector('.cfg-lightbox__sheen');
      lbEl.querySelector('.cfg-lightbox__close').addEventListener('click', lbClose);
      // Backdrop tap (not a pan/pinch release, not the media) closes.
      lbEl.addEventListener('click', function (e) {
        if (lbMoved) { lbMoved = false; return; }
        if (!e.target.closest('.cfg-lightbox__zoom')) lbClose();
      });
      // Desktop wheel zoom.
      lbPersp.addEventListener('wheel', function (e) {
        e.preventDefault();
        lbSetZoom(lbZ + (e.deltaY < 0 ? 0.2 : -0.2));
      }, { passive: false });
      // Double-click (desktop) / manual double-tap (touch) toggles zoom.
      lbTiltEl.addEventListener('pointerup', function (e) {
        if (lbMoved) return;
        var now = performance.now();
        if (now - lbLastTap < 300) { lbSetZoom(lbZ > 1 ? 1 : LB_MAXZOOM); lbLastTap = 0; }
        else lbLastTap = now;
      });
      // Pointer fallback tilt (ignored once real orientation data arrives),
      // plus drag-pan and two-pointer pinch on the same pointer stream.
      lbPersp.addEventListener('pointerdown', function (e) {
        lbPointers[e.pointerId] = { x: e.clientX, y: e.clientY, sx: e.clientX, sy: e.clientY };
        var ids = Object.keys(lbPointers);
        if (ids.length === 2) {
          var a = lbPointers[ids[0]], b = lbPointers[ids[1]];
          lbPinchBase = { d: Math.hypot(a.x - b.x, a.y - b.y), z: lbZ };
        }
        lbMoved = false;
      });
      lbPersp.addEventListener('pointermove', function (e) {
        var p = lbPointers[e.pointerId];
        var ids = Object.keys(lbPointers);
        if (p && ids.length === 2 && lbPinchBase) {
          // Pinch zoom + two-finger pan.
          p.x = e.clientX; p.y = e.clientY;
          var a = lbPointers[ids[0]], b = lbPointers[ids[1]];
          lbSetZoom(lbPinchBase.z * (Math.hypot(a.x - b.x, a.y - b.y) / lbPinchBase.d));
          lbMoved = true;
          return;
        }
        if (p && lbZ > 1) {
          // One-pointer drag pan while zoomed (desktop fallback + touch).
          lbTX += e.clientX - p.x; lbTY += e.clientY - p.y;
          p.x = e.clientX; p.y = e.clientY;
          // >3px total travel = a real drag (touch taps jitter ~1px — those
          // must still register as taps for the double-tap zoom toggle).
          if (Math.abs(e.clientX - p.sx) + Math.abs(e.clientY - p.sy) > 3) lbMoved = true;
          lbApplyZoom();
          return;
        }
        if (!lbGyro && !p) {
          // Hover tilt (no button held): same tilt the gyro drives.
          lbLastInput = performance.now();
          var r = lbPersp.getBoundingClientRect();
          lbSetTilt(-((e.clientY - r.top) / r.height - 0.5) * 20,
                     ((e.clientX - r.left) / r.width - 0.5) * 20);
        }
      });
      function lbPointerEnd(e) {
        delete lbPointers[e.pointerId];
        if (Object.keys(lbPointers).length < 2) lbPinchBase = null;
      }
      lbPersp.addEventListener('pointerup', lbPointerEnd);
      lbPersp.addEventListener('pointercancel', lbPointerEnd);
    }
    function lbKeyHandler(e) {
      if (e.key === 'Escape' && lbEl && !lbEl.hidden) { e.stopPropagation(); lbClose(); }
    }
    function lbOpen(srcEl) {
      if (!lbEl) lbBuild();
      var isImg = srcEl.tagName === 'IMG';
      lbCanvas.hidden = isImg;
      lbImg.hidden = !isImg;
      if (isImg) {
        lbImg.src = srcEl.currentSrc !== '' ? srcEl.currentSrc : srcEl.src;
      } else {
        lbCanvas.width = srcEl.width; lbCanvas.height = srcEl.height;
        var ctx = lbCanvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, lbCanvas.width, lbCanvas.height);
          try { ctx.drawImage(srcEl, 0, 0); } catch (e) {}
        }
      }
      lbZ = 1; lbTX = 0; lbTY = 0; lbMoved = false;
      lbBeta0 = null; lbGamma0 = null; // recalibrate neutral each open
      lbZoomEl.style.transform = '';
      if (lbTiltEl) lbTiltEl.style.transform = '';
      if (lbSheen) lbSheen.style.opacity = '0';
      lbPrevOverflow = [document.documentElement.style.overflow, document.body.style.overflow];
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      lbEl.hidden = false;
      document.addEventListener('keydown', lbKeyHandler, true);
      lbEnsureTiltDriver(); // inside the opening tap's user gesture
      lbLastInput = performance.now();
      if (!prefersReduced && lbDriftRaf == null) lbDriftRaf = requestAnimationFrame(lbDrift);
    }
    function lbClose() {
      if (!lbEl || lbEl.hidden) return;
      lbEl.hidden = true;
      document.removeEventListener('keydown', lbKeyHandler, true);
      window.removeEventListener('deviceorientation', lbOrientHandler);
      if (lbDriftRaf != null) { cancelAnimationFrame(lbDriftRaf); lbDriftRaf = null; }
      if (lbPrevOverflow) {
        document.documentElement.style.overflow = lbPrevOverflow[0];
        document.body.style.overflow = lbPrevOverflow[1];
        lbPrevOverflow = null;
      }
      lbPointers = {}; lbPinchBase = null;
    }
    function lbStageClick(e) {
      // Phase 8 Push 2: media-scoped — taps on the captions/prices inside an
      // item no longer open the inspect lightbox; the renders still do.
      var media = e.target.closest ? e.target.closest('.cfg-finale__item canvas, .cfg-finale__item img') : null;
      if (!media || media.hidden) return;
      lbOpen(media);
    }

    // Phase 5 Fix 1: a COMPLETED tap (click) inside any step square hides the
    // finale — a touch that starts a scroll fires no click, so scrolling can
    // never hide it or disturb step 4's state.
    root.addEventListener('click', function (e) {
      if (!finale || finale.hidden) return;
      if (e.target.closest && e.target.closest('.cfg-step')) exitFinale(false);
    }, true);

    // Phase 3 Task 5b: the finale's proxy ATC is GONE — the native
    // product-form button is the one ATC (it already submits the full bundle
    // through configurator.js's interception). This passive capture listener
    // only stamps finaleAwaitingGate when the native button is clicked while
    // the finale is visible, so the gate-failure auto-exit branch keeps
    // working. No clicks are dispatched; no cart logic is touched.
    document.addEventListener('click', function (e) {
      if (!finale || finale.hidden) return;
      var btn = e.target.closest ? e.target.closest('button[type="submit"], button[name="add"]') : null;
      if (!btn) return;
      // Phase 8 Push 3 (Task 2, load-bearing): the relocated finale ATC is
      // form-attribute-associated, so closest() no longer sees its form —
      // btn.form still resolves the association. Either match stamps.
      var inCartForm = !!btn.closest('form[is="product-form"], form[action*="/cart/add"]');
      var viaFormAttr = !!(btn.form && (btn.form.getAttribute('action') ?? '').indexOf('/cart/add') >= 0);
      if (inCartForm || viaFormAttr) {
        finaleAwaitingGate = Date.now();
      }
    }, true);

    // --- Phase 8 Push 3 (Task 1): native ATC relocated into the finale ----
    // The theme's PRIMARY submit button (inside the product form within this
    // product-info container — quick-buy card forms are outside it) moves
    // into the finale's slot as a real node, gaining form="<form id>" so
    // submission still fires the product form's submit event (configurator's
    // interception untouched; the theme's own sticky externals use the same
    // attribute pattern). A stepper-owned count span renders "— N cases";
    // the theme's own label content is never touched.
    var atcSlot = finale ? finale.querySelector('[data-cfg-finale-atc-slot]') : null;
    var atcBtn = null, atcCountEl = null;
    function refreshAtcCount() {
      if (!atcBtn) return;
      var n = Math.max(1, casesEl ? casesEl.querySelectorAll('.cfg-case-tab').length : 1);
      if (!atcCountEl || !atcBtn.contains(atcCountEl)) {
        atcCountEl = document.createElement('span');
        atcCountEl.className = 'cfg-finale__atc-count';
        atcBtn.appendChild(atcCountEl);
      }
      atcCountEl.textContent = ' — ' + n + (n === 1 ? ' case' : ' cases');
    }
    (function relocateAtc() {
      if (!atcSlot) return;
      var pi = root.closest('.product-info') ?? document;
      var btn = pi.querySelector('form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] button[name="add"]');
      var form = btn ? btn.closest('form') : null;
      // getAttribute, NOT form.id — the product form contains <input name="id">,
      // which SHADOWS the id property with the element itself (audit §5 gotcha).
      var formId = form ? form.getAttribute('id') : null;
      if (!btn || formId == null || formId === '') return; // theme layout changed — leave the ATC in place
      btn.setAttribute('form', formId);
      atcSlot.appendChild(btn);
      atcBtn = btn;
      // E2E-measured (Push 3): once the button was relocated, plain clicks
      // died silently, so this listener restores submission via
      // requestSubmit() — a REAL submit event (constraint-validated) that
      // configurator.js intercepts exactly as before.
      // Push 4 Task 3: exactly ONE submit per user click, theme path dead OR
      // alive. Mechanism, chosen after re-reading the theme as deployed:
      // CustomButton (theme.js custom-button.js) attaches NO click handler —
      // it only animates on aria-busy — and ProductForm binds _onSubmit on
      // the form itself, which configurator's document-CAPTURE submit
      // interceptor pre-empts with stopImmediatePropagation(). That same
      // interceptor means a FORM-level capture listener can never swallow a
      // duplicate submit (stopImmediatePropagation at document capture kills
      // dispatch before any form-level listener runs). The only vantage
      // point that fires earlier is WINDOW capture (window → document → …).
      // So, three cooperating pieces:
      //   1. document-capture click/keydown listeners open a fresh "user
      //      input tick" (counter = 0) BEFORE any button-level handler runs;
      //   2. a window-capture submit listener counts submits of OUR form in
      //      the tick and swallows every one after the first — before
      //      configurator could double-build/double-POST;
      //   3. our requestSubmit is DEFERRED to setTimeout(0), i.e. after the
      //      click's default action: it fires only when the click produced
      //      no submit by itself (the dead-path state measured on this
      //      theme). If a theme update revives native/handler submission,
      //      the deferral sees that submit and stands down.
      var atcSubmitsThisTick = 0;
      document.addEventListener('click', function () { atcSubmitsThisTick = 0; }, true);
      document.addEventListener('keydown', function () { atcSubmitsThisTick = 0; }, true);
      window.addEventListener('submit', function (e) {
        if (e.target !== form) return;
        atcSubmitsThisTick += 1;
        if (atcSubmitsThisTick > 1) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
      btn.addEventListener('click', function () {
        setTimeout(function () {
          if (atcSubmitsThisTick > 0) return; // the click already submitted — stand down
          try { form.requestSubmit(btn); }
          catch (err) { try { form.requestSubmit(); } catch (err2) {} }
        }, 0);
      });
      refreshAtcCount();
    })();

    // --- Engraving-gallery relocation (<1000px, Phase 2.7) -----------------
    // The vs-engraving-gallery section's OWN script slots its node into
    // [data-vseg-mobile-slot]; once that happened, move the node directly
    // after the buy-buttons block. Poll ~3s; if slot/node never appears, do
    // nothing. Desktop slotting untouched. Inherently template-gated: this
    // code only runs where the .cfg-step wrappers exist.
    function relocateVsegGallery() {
      if (!mqMobile.matches) return;
      var buyWrap = document.querySelector('.product-info__buy-buttons');
      if (!buyWrap) return;
      var tries = 0;
      (function poll() {
        var slot = document.querySelector('[data-vseg-mobile-slot]');
        var node = slot ? slot.firstElementChild : null;
        if (node) {
          if (buyWrap.nextElementSibling !== node) buyWrap.after(node);
          return;
        }
        if (++tries < 15) setTimeout(poll, 200);
      })();
    }
    relocateVsegGallery();

    // --- Boot --------------------------------------------------------------
    if (panel) panel.hidden = false;
    if (nonePill) nonePill.hidden = false;
    if (walletNone) walletNone.hidden = false;
    continueBtns.forEach(function (b) { b.hidden = false; });
    if (finishBtn) finishBtn.hidden = false;
    renderAll();
    startMirror();
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[id^="configurator-"]');
    for (var i = 0; i < els.length; i++) initStepper(els[i]);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else { boot(document); }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target || document); });
})();
